import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { get, patch, post, qs } from '../lib/api';
import { navigate, useDebounced, useLoad } from '../lib/hooks';
import { usePlan } from '../lib/plan';
import { INDUSTRIES, isIndustryId } from '../../shared/industries';
import { useSession } from '../lib/session';
import { useCart } from '../lib/cart';
import { formatCents } from '../../shared/money';
import { CARRIERS, CONDITION_LABELS, DEVICE_STATUS_LABELS, GRADE_LABELS, formatDateTime, isValidImei, zone } from '../../shared/format';
import { Badge, Button, Confirm, Cost, Empty, Field, Money, MoneyInput, Profit, Sheet, Spinner, Tabs, enterToNextBox, statTone, useAction, useToast, type ApprovalInput } from '../ui/kit';
import { Icon } from '../ui/icons';
import { ProductPicture } from '../ui/art';
import { DevicePhoto, useModelPhoto } from '../ui/photo';
import { UndoWholesaleSheet, WHOLESALE_STATUSES, WholesaleInfo, WholesaleSheet } from './Wholesale';
import { StickerEditor, batteryProblem, draftFromPhone, lastPriceLike, printPriceStickers, stickerFromDraft, type PricedLike, type StickerDraft } from './PriceSticker';
import { usePrint } from '../ui/print';
import { photoToDataUrl, pickPhoto } from '../lib/image';
import { BulkEditSheet, type BulkField, type BulkValues } from '../ui/bulk';

type Tab = 'devices' | 'products' | 'parts' | 'low' | 'transfers';
const DEVICE_CATS = [
  { id: 'phones', label: 'Phones' },
  { id: 'tablets', label: 'Tablets' },
  { id: 'ipads', label: 'iPads' },
  { id: 'macbooks', label: 'MacBooks' },
  { id: 'consoles', label: 'Gaming consoles' },
  { id: 'other_devices', label: 'Other devices (watches, earbuds, laptops…)' },
];
/** The brand the Add box starts with for each type; blank means "type it or leave it". */
const BRAND_DEFAULT: Record<string, string> = { phones: 'Apple', ipads: 'Apple', macbooks: 'Apple' };
const CONSOLE_BRANDS = ['Sony', 'Microsoft', 'Nintendo', 'Meta', 'Valve'];
/** What a device is called when nobody typed a model: the kind of thing it is. */
const MODEL_DEFAULT: Record<string, string> = { phones: 'Phone', tablets: 'Tablet', ipads: 'iPad', macbooks: 'MacBook', consoles: 'Gaming console', other_devices: 'Device' };
const MODEL_HINT: Record<string, string> = {
  phones: 'e.g. iPhone 16 Pro Max', tablets: 'e.g. Galaxy Tab S10', ipads: 'e.g. iPad Air 11-inch', macbooks: 'e.g. MacBook Air 13-inch',
  consoles: 'e.g. PlayStation 5 Slim, Xbox Series X, Switch 2', other_devices: 'e.g. Apple Watch Ultra 3, AirPods Pro 3',
  laptops: 'e.g. Dell XPS 13, ThinkPad T14', desktops: 'e.g. OptiPlex 7010, iMac 24-inch', vacuums: 'e.g. Dyson V11, Shark Navigator',
};

/**
 * What each kind of shop keeps in stock as single units with a serial number (phones; laptops and
 * desktops; vacuums), and what its parts and products tab is called. An auto shop has parts only.
 */
function stockSetup(industry: string, home: boolean) {
  if (industry === 'computer') {
    return {
      units: { tab: 'Computers', add: 'Add a computer', title: 'Add a computer', search: 'Serial number or model', cats: [{ id: 'laptops', label: 'Laptops' }, { id: 'desktops', label: 'Desktops' }, { id: 'macbooks', label: 'MacBooks' }, { id: 'tablets', label: 'Tablets' }, { id: 'other_devices', label: 'Other devices' }], phones: false, storage: true, brands: ['Apple', 'Dell', 'HP', 'Lenovo', 'ASUS', 'Acer', 'Microsoft'] },
      productsTab: 'Parts & accessories',
    };
  }
  if (industry === 'vacuum') {
    return {
      units: { tab: 'Vacuums', add: 'Add a vacuum', title: 'Add a vacuum', search: 'Serial number or model', cats: [{ id: 'vacuums', label: 'Vacuums' }, { id: 'other_devices', label: 'Other machines' }], phones: false, storage: false, brands: ['Dyson', 'Shark', 'Hoover', 'Bissell', 'Miele', 'Kirby', 'Oreck'] },
      productsTab: 'Parts, bags & filters',
    };
  }
  if (industry === 'auto') return { units: null, productsTab: 'Parts & supplies' };
  return {
    units: { tab: 'Phones & devices', add: 'Add phone or device', title: 'Add a phone or device', search: 'IMEI, serial or model', cats: DEVICE_CATS, phones: true, storage: true, brands: ['Apple', 'Samsung', 'Google', 'Motorola', 'Sony'] },
    productsTab: home ? 'Accessories, perfume & parts' : 'Accessories & parts',
  };
}

/** The product categories a shop that isn't a phone shop filters by: its own starting ones. */
function shopProductCats(industry: string): { id: string; label: string }[] {
  const ind = INDUSTRIES[isIndustryId(industry) ? industry : 'cellphone'];
  const cats = [{ id: '', label: 'Everything' }, ...ind.categories.filter((c) => c.tracking === 'quantity').map((c) => ({ id: c.id, label: c.name }))];
  if (industry === 'computer') cats.push({ id: 'chargers', label: 'Chargers' }, { id: 'accessories', label: 'Accessories' });
  if (industry === 'vacuum') cats.push({ id: 'accessories', label: 'Accessories' });
  return cats;
}

/** The brand a model gives away ("PS5" is Sony, "Switch 2" is Nintendo, "AirPods" are Apple), or null. */
function brandFromModel(model: string): string | null {
  const m = model.toLowerCase();
  if (/playstation|\bps[1-5]\b|\bpsp\b|ps vita/.test(m)) return 'Sony';
  if (/xbox/.test(m)) return 'Microsoft';
  if (/nintendo|switch|\bwii\b|game ?boy|\b[23]ds\b/.test(m)) return 'Nintendo';
  if (/quest|oculus/.test(m)) return 'Meta';
  if (/steam ?deck/.test(m)) return 'Valve';
  if (/iphone|ipad|macbook|imac|airpods|apple|ipod/.test(m)) return 'Apple';
  if (/galaxy|samsung/.test(m)) return 'Samsung';
  if (/pixel/.test(m)) return 'Google';
  return null;
}
const STATUS_TONE: Record<string, 'green' | 'amber' | 'blue' | 'red' | 'plain'> = { in_stock: 'green', testing: 'amber', on_hold: 'amber', in_transit: 'blue', sold: 'plain', parts_only: 'plain', removed: 'red' };
const REASONS: Record<string, string> = { receive: 'Received', sale: 'Sold', refund: 'Refund', adjust: 'Count corrected', transfer_out: 'Sent to another store', transfer_in: 'Received from another store', transfer_cancel: 'Transfer cancelled', add: 'Added', buyback: 'Bought from customer', trade_in: 'Trade-in', removed: 'Deleted from stock (no sale)', wholesale: 'Sold wholesale', wholesale_undo: 'Wholesale undone' };

/** The most of one item received, counted or sent at once (the server says the same). */
const MAX_LINE_QTY = 10_000;

/** "Nov 12": the day a bought phone's hold ends. */
function holdDay(ms: number, tz?: string | null): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz || zone.current, month: 'short', day: 'numeric' }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

/** A bought phone on the hold: it shows In stock but can't be sold until the hold ends. */
function HoldBadge({ device }: { device: { held?: boolean; hold_until: number | null } }) {
  const { me } = useSession();
  if (!device.held || !device.hold_until) return null;
  return <Badge tone="amber" icon="clock">On hold until {holdDay(device.hold_until, me?.settings?.timezone)}</Badge>;
}

/**
 * A barcode scanner types the code in one quick run of keys, then presses Enter. `run` is what was
 * typed since the last pause (a quarter second), with when it started. It's a scan when Enter comes
 * right after the run, and the run is long (no count has 6 digits) or came at scanner speed.
 */
interface KeyRun { run: string; start: number; last: number }
const PAUSE_MS = 250;

function addKey(r: KeyRun, key: string, now: number): boolean {
  const paused = now - r.last >= PAUSE_MS;
  if (paused) {
    r.run = '';
    r.start = now;
  }
  r.run += key;
  r.last = now;
  return paused;
}

function isScan(r: KeyRun, now: number): boolean {
  if (now - r.last >= PAUSE_MS || r.run.length < 4) return false;
  return r.run.length >= 6 || (now - r.start) / r.run.length < 50;
}

/**
 * For a count box. Enter never saves from it, and a barcode scanned into it comes back out:
 * `onScan` gets the code and what the box said before, so the count goes back and the scan
 * counts as a scan.
 */
function useScanGuard() {
  const g = useRef<KeyRun & { before: string }>({ run: '', start: 0, last: 0, before: '' });
  return useCallback((e: ReactKeyboardEvent<HTMLInputElement>, onScan: (code: string, before: string) => void) => {
    const s = g.current;
    // When the key was pressed, not when it got here (the screen redrawing can hold keys back).
    const now = e.timeStamp || performance.now();
    if (e.key === 'Enter') {
      e.preventDefault();
      const scanned = isScan(s, now);
      const code = s.run;
      s.run = '';
      if (scanned) onScan(code, s.before);
      return;
    }
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
    const before = e.currentTarget.value;
    // After a pause, what the box says is what it said before anything scanned next.
    if (addKey(s, e.key, now)) s.before = before;
  }, []);
}

/**
 * A barcode scanned while no box has the cursor (after tapping + or −) goes to `onScan`, and
 * only there: it's caught before the app's scan-to-open, which would open the item instead.
 */
function useSheetScanner(onScan: (code: string) => void) {
  const ref = useRef(onScan);
  ref.current = onScan;
  useEffect(() => {
    const r: KeyRun = { run: '', start: 0, last: 0 };
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const now = e.timeStamp || performance.now();
      if (e.key === 'Enter') {
        // Nobody types letters with a button in focus: a run of 4 or more, then Enter, is a barcode.
        if (now - r.last < PAUSE_MS && r.run.length >= 4) {
          e.preventDefault();
          e.stopImmediatePropagation();
          ref.current(r.run);
        }
        r.run = '';
      } else if (e.key.length === 1) {
        addKey(r, e.key, now);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
}

export function InventoryScreen({ tab, query }: { tab?: string; query: URLSearchParams }) {
  const { me } = useSession();
  const plan = usePlan();
  const stock = stockSetup(plan.industry, plan.home);
  const first: Tab = stock.units ? 'devices' : 'products';
  const asked = (['devices', 'products', 'parts', 'low', 'transfers'].includes(tab ?? '') ? tab : first) as Tab;
  const current: Tab = asked === 'devices' && !stock.units ? 'products' : asked;
  const [loc, setLoc] = useState<string>(me?.location?.id ?? 'all');
  return (
    <div className="page">
      <div className="page-head">
        <h1>Inventory</h1>
        {(me?.locations.length ?? 0) > 1 ? (
          <select className="select" style={{ width: 'auto' }} value={loc} onChange={(e) => setLoc(e.target.value)} aria-label="Store">
            {me?.locations.map((l) => <option key={l.id} value={l.id}>{l.name} inventory</option>)}
            <option value="all">All stores</option>
          </select>
        ) : null}
      </div>
      <Tabs label="Inventory sections" value={current} onChange={(t) => navigate(`/inventory/${t}`)} tabs={[...(stock.units ? [{ id: 'devices', label: stock.units.tab }] : []), { id: 'products', label: stock.productsTab }, { id: 'parts', label: 'Parts' }, { id: 'low', label: 'Low stock' }, { id: 'transfers', label: 'Transfers' }]} />
      {current === 'devices' || current === 'products' || current === 'parts' ? <InventoryTotals loc={loc} section={current} /> : null}
      {current === 'devices' ? <DevicesTab loc={loc} openId={query.get('id')} status={query.get('status')} /> : null}
      {current === 'products' ? <ProductsTab loc={loc} openId={query.get('id')} /> : null}
      {current === 'parts' ? <ProductsTab loc={loc} openId={query.get('id')} parts /> : null}
      {current === 'low' ? <LowTab loc={loc} /> : null}
      {current === 'transfers' ? <TransfersTab /> : null}
    </div>
  );
}

// ---------------- totals on top of every section ----------------

interface StockTotals { count: number; items?: number; retail_cents: number; cost_cents: number | null }
interface StockSummary { month_start: string; month_label: string; devices: { now: StockTotals; start: StockTotals }; products: { now: StockTotals; start: StockTotals }; parts: { now: StockTotals; start: StockTotals } }

/** "+3" or "−$120.00": how much more (or less) than the month started with. */
function delta(now: number, start: number, money = false): string {
  const d = now - start;
  const n = money ? formatCents(Math.abs(d)) : String(Math.abs(d));
  return d === 0 ? (money ? 'same' : '±0') : `${d > 0 ? '+' : '−'}${n}`;
}

/**
 * What's in stock now and what the month started with: how many, what it all cost (the owner's)
 * and what it sells for. The same tiles on top of Phones, Products and Parts.
 */
function InventoryTotals({ loc, section }: { loc: string; section: 'devices' | 'products' | 'parts' }) {
  const sum = useLoad<StockSummary>(() => get(`/api/inventory/summary${qs({ location: loc })}`), [loc]);
  const d = sum.data?.[section];
  if (!d) return sum.error ? null : <div className="stat-row inv-totals" aria-busy="true"><div className="stat"><div className="k">In stock now</div><div className="v">…</div></div></div>;
  const word = section === 'devices' ? (d.now.count === 1 ? 'device' : 'devices') : d.now.count === 1 ? 'unit' : 'units';
  const items = d.now.items !== undefined ? ` · ${d.now.items} item${d.now.items === 1 ? '' : 's'}` : '';
  const money = d.now.cost_cents !== null && d.start.cost_cents !== null;
  return (
    <div className="stat-row inv-totals" aria-label="Inventory totals">
      <div className="stat"><div className="k">In stock now</div><div className="v">{d.now.count.toLocaleString('en-US')} <span className="small muted">{word}</span></div><div className="small muted">{delta(d.now.count, d.start.count)} since {sum.data!.month_label.split(' ')[0]} 1{items}</div></div>
      {money ? <div className="stat stat-cost"><div className="k">Total cost</div><div className="v">{formatCents(d.now.cost_cents!)}</div><div className="small">{delta(d.now.cost_cents!, d.start.cost_cents!, true)} since the 1st</div></div> : null}
      <div className="stat stat-profit"><div className="k">Total retail</div><div className="v">{formatCents(d.now.retail_cents)}</div><div className="small">{delta(d.now.retail_cents, d.start.retail_cents, true)} since the 1st</div></div>
      <div className="stat"><div className="k">Started {sum.data!.month_label} with</div><div className="v">{d.start.count.toLocaleString('en-US')} <span className="small muted">{section === 'devices' ? (d.start.count === 1 ? 'device' : 'devices') : d.start.count === 1 ? 'unit' : 'units'}</span></div><div className="small muted">{money ? `cost ${formatCents(d.start.cost_cents!)} · ` : ''}retail {formatCents(d.start.retail_cents)}</div></div>
    </div>
  );
}

// ---------------- phones & devices ----------------

/** A phone that's for sale with a price: it can have a price sticker. */
const stickerable = (d: { status: string; price_cents: number }) => (d.status === 'in_stock' || d.status === 'on_hold') && d.price_cents > 0;
/** A phone that can be deleted from stock: not sold, not already out, not on its way to another store. */
const removable = (d: { status: string }) => !['sold', 'removed', 'in_transit'].includes(d.status);

function DevicesTab({ loc, openId, status: initialStatus }: { loc: string; openId: string | null; status: string | null }) {
  const { can, me } = useSession();
  const plan = usePlan();
  const units = stockSetup(plan.industry, plan.home).units ?? stockSetup('cellphone', plan.home).units!;
  const print = usePrint();
  const [picked, setPicked] = useState<string[]>([]);
  const [status, setStatus] = useState(initialStatus ?? 'in_stock');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const [adding, setAdding] = useState(false);
  // The price sticker being edited before it prints (the tag button in the list).
  const [sticker, setSticker] = useState<any | null>(null);
  // Several ticked phones at once: remove them from inventory, or change their price, status or grade.
  const [removingMany, setRemovingMany] = useState(false);
  const [editingMany, setEditingMany] = useState(false);
  const [open, setOpen] = useState<string | null>(openId);
  useEffect(() => setOpen(openId), [openId]);
  const list = useLoad(() => get(`/api/devices${qs({ location: loc, status, category, q: dq, limit: 500 })}`), [loc, status, category, dq]);
  const showCost = can('inventory.view_cost');
  const value = (list.data ?? []).reduce((s: number, d: any) => s + (showCost ? d.cost_cents ?? 0 : d.price_cents), 0);
  const rows: any[] = list.data ?? [];
  // Tick phones (or Select all): print their price stickers, edit them or remove them from inventory in one go.
  const chosen = rows.filter((d) => picked.includes(d.id));
  const chosenPrintable = chosen.filter(stickerable);
  const chosenEditable = chosen.filter((d) => d.status !== 'sold');
  const chosenRemovable = chosen.filter(removable);
  const allPicked = rows.length > 0 && chosen.length === rows.length;
  const canRemove = can('inventory.adjust');
  const canEditMany = can('device.edit_price') || can('inventory.adjust') || can('device.add');
  // Bought phones still on the hold: listed after the ones that can be sold.
  const heldCount = rows.filter((d: any) => d.held).length;
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const printStickers = (ds: any[]) => printPriceStickers(print, ds, me?.settings);
  return (
    <section className="stack">
      <div className="row-wrap">
        <div className="search grow" style={{ minWidth: 240 }}>
          <Icon.search />
          <input className="input" placeholder={units.search} value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search devices" />
        </div>
        <select className="select" style={{ width: 'auto' }} value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
          <option value="">{units.phones ? 'All device types' : 'All types'}</option>
          {units.cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        {can('device.add') ? <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>{units.add}</Button> : null}
      </div>
      <div className="chips">
        {['in_stock', 'testing', 'on_hold', 'in_transit', 'parts_only', 'sold', 'any'].map((s) => (
          <button key={s} type="button" className="chip" aria-pressed={status === s} onClick={() => setStatus(s)}>{s === 'any' ? 'Everything' : DEVICE_STATUS_LABELS[s]}</button>
        ))}
      </div>
      {list.loading && !list.data ? <Spinner /> : null}
      {list.data ? (
        list.data.length ? (
          <>
            <div className="row-wrap">
              <p className="muted small grow" style={{ margin: 0 }}>
                {list.data.length} device{list.data.length === 1 ? '' : 's'} · {heldCount ? <><strong>{heldCount} on hold</strong> (can’t be sold yet, listed last) · </> : null}{showCost ? 'cost' : 'retail'} value {formatCents(value)}
              </p>
              {chosen.length ? (
                <>
                  <span className="small strong">{chosen.length} picked</span>
                  {chosenPrintable.length ? <Button size="sm" variant="primary" icon="tag" onClick={() => printStickers(chosenPrintable)}>Print {chosenPrintable.length} price sticker{chosenPrintable.length === 1 ? '' : 's'}</Button> : null}
                  {canEditMany && chosenEditable.length ? <Button size="sm" icon="edit" onClick={() => setEditingMany(true)}>Edit {chosenEditable.length}</Button> : null}
                  {canRemove && chosenRemovable.length ? <Button size="sm" variant="danger" icon="trash" onClick={() => setRemovingMany(true)}>Remove {chosenRemovable.length} from inventory</Button> : null}
                  <Button size="sm" variant="ghost" onClick={() => setPicked([])}>Clear</Button>
                </>
              ) : <span className="small muted">Tick {units.phones ? 'phones' : units.tab.toLowerCase()} (or Select all) to print price stickers, edit them or remove them from inventory</span>}
            </div>
            <div className="list">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <input type="checkbox" className="row-pick" checked={allPicked} onChange={() => setPicked(allPicked ? [] : rows.map((d) => d.id))} aria-label="Select all" title="Select all" />
                    </th>
                    <th style={{ width: 64 }} /><th>Device</th><th>Store</th><th>Battery</th><th className="right">Price</th>{showCost ? <th className="right">Cost</th> : null}{showCost ? <th className="right">Profit</th> : null}<th>Status</th><th style={{ width: 48 }} />
                  </tr>
                </thead>
                <tbody>
                  {list.data.map((d: any) => (
                    <tr key={d.id} className="click" onClick={() => setOpen(d.id)}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="row-pick" checked={picked.includes(d.id)} onChange={() => toggle(d.id)} aria-label={`Pick ${d.title}`} />
                      </td>
                      <td><span className="thumb" style={{ width: 48, height: 48 }}><DevicePhoto url={d.image_url} brand={d.brand} model={d.model} color={d.color} category={d.category_id} size={48} /></span></td>
                      <td><div className="strong">{d.title}</div><div className="small muted">{d.detail}</div></td>
                      <td>{d.location_name}</td>
                      <td>{d.battery_health ? `${d.battery_health}%` : '—'}</td>
                      <td className="right strong"><Money cents={d.price_cents} /></td>
                      {showCost ? <td className="right"><Cost cents={d.cost_cents} label={false} /></td> : null}
                      {showCost ? <td className="right"><Profit cents={d.profit_cents} label={false} /></td> : null}
                      <td><div className="row-wrap" style={{ gap: 4 }}><Badge tone={STATUS_TONE[d.status] ?? 'plain'}>{DEVICE_STATUS_LABELS[d.status] ?? d.status}</Badge><HoldBadge device={d} /></div></td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {stickerable(d) ? <Button size="sm" variant="ghost" icon="tag" aria-label={`Price sticker for ${d.title}`} title="Edit and print its price sticker" onClick={() => setSticker(d)} /> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <Empty icon="boxes" title="No devices match." />
        )
      ) : null}
      {adding ? <AddDeviceSheet onClose={() => setAdding(false)} onDone={() => { setAdding(false); list.reload(); }} /> : null}
      {open ? <DeviceSheet id={open} onClose={() => { setOpen(null); if (openId) navigate('/inventory/devices'); }} onChanged={list.reload} /> : null}
      {sticker ? <StickerSheet device={sticker} onClose={() => setSticker(null)} onSaved={list.reload} /> : null}
      {removingMany ? <RemoveManySheet devices={chosenRemovable} onClose={() => setRemovingMany(false)} onDone={() => { setRemovingMany(false); setPicked([]); list.reload(); }} /> : null}
      {editingMany ? <EditManySheet devices={chosenEditable} onClose={() => setEditingMany(false)} onDone={() => { setEditingMany(false); setPicked([]); list.reload(); }} /> : null}
    </section>
  );
}

/** What another screen already knows about the phone (Phone check fills this in from a SICKW check). */
export interface DevicePrefill {
  categoryId?: string; brand?: string; model?: string; storage?: string; color?: string; carrier?: string; locked?: boolean;
  imei?: string; imei2?: string; serial?: string; notes?: string;
}

/**
 * Add a phone, a gaming console or any other device. Nothing is required: whatever is left blank is
 * filled in (the brand from the model — "PS5" is Sony — or the type's usual brand, the model from the
 * type — "Gaming console", the price $0 until someone prices it, and a stock number in place of an IMEI
 * or serial, so the item still gets a barcode to scan at the register). `prefill`: boxes already
 * filled in (from a Phone check); `onDone` gets the device that was added.
 */
export function AddDeviceSheet({ prefill, onClose, onDone }: { prefill?: DevicePrefill | null; onClose: () => void; onDone: (device?: any) => void }) {
  const { can, me } = useSession();
  // A computer or vacuum shop adds its own kind of unit: a serial number, no IMEI or carrier.
  const plan = usePlan();
  const units = stockSetup(plan.industry, plan.home).units ?? stockSetup('cellphone', plan.home).units!;
  const print = usePrint();
  const { run, busy } = useAction();
  const catalog = useLoad(() => get('/api/device-catalog'), []);
  const suppliers = useLoad(() => get('/api/suppliers'), []);
  const [f, setF] = useState(() => {
    // A carrier SICKW named that isn't on the list: "Other" when the phone is locked to it, else Unlocked.
    const list = CARRIERS as readonly string[];
    const carrier = prefill?.carrier && list.includes(prefill.carrier) ? prefill.carrier : prefill?.locked ? list.find((c) => /^other/i.test(c)) ?? 'Unlocked' : 'Unlocked';
    const categoryId = prefill?.categoryId || units.cats[0].id;
    return {
      categoryId, brand: prefill?.brand || BRAND_DEFAULT[categoryId] || '', model: prefill?.model ?? '', storage: prefill?.storage ?? '', color: prefill?.color ?? '',
      carrier, locked: !!prefill?.locked && carrier !== 'Unlocked', condition: 'used', grade: 'A',
      imei: prefill?.imei ?? '', imei2: prefill?.imei2 ?? '', serial: prefill?.serial ?? '', batteryHealth: '', supplierId: '', notes: prefill?.notes ?? '',
    };
  });
  // Until someone types a brand, changing the type swaps in that type's usual brand (Apple for phones, none for consoles).
  const [brandTyped, setBrandTyped] = useState(!!prefill?.brand);
  const setType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value;
    setF({ ...f, categoryId, brand: brandTyped ? f.brand : BRAND_DEFAULT[categoryId] ?? '' });
  };
  // What goes to the server when the boxes are blank (see the note above).
  const modelSent = f.model.trim() || MODEL_DEFAULT[f.categoryId] || 'Device';
  const brandSent = f.brand.trim() || brandFromModel(modelSent) || modelSent;
  const stockNo = useMemo(() => `STK-${String(Date.now()).slice(-8)}`, []);
  const [price, setPrice] = useState<number | null>(null);
  const [cost, setCost] = useState<number | null>(null);
  // Its picture: a photo taken here, or by itself the picture of its model (the iPhone 11 for an iPhone 11).
  const toast = useToast();
  const [photo, setPhoto] = useState('');
  const [uploading, setUploading] = useState(false);
  const modelPhoto = useModelPhoto(f.model);
  const modelBox = useRef<HTMLInputElement>(null);
  const takePhoto = async () => {
    const file = await pickPhoto({ camera: true });
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await photoToDataUrl(file, 1000, 0.85);
      const res = await post('/api/images', { dataUrl });
      setPhoto(res.url);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That picture could not be saved.', 'error');
    } finally {
      setUploading(false);
    }
  };
  // The price is remembered: what the store last asked for one like it (same brand, model, storage
  // and carrier) fills the box in, until someone types a price of their own.
  const spec = useDebounced(`${brandSent}|${f.model}|${f.storage}|${f.carrier}|${f.carrier !== 'Unlocked' && f.locked ? 'locked' : ''}`, 400);
  const like = useLoad<PricedLike | null>(() => {
    const [brand, model, storage, carrier, locked] = spec.split('|');
    return model.trim() ? lastPriceLike({ brand, model, storage, carrier, locked: locked === 'locked' }) : Promise.resolve(null);
  }, [spec]);
  const suggested = useRef<number | null>(null);
  useEffect(() => {
    if (like.loading) return;
    const cents = like.data?.price_cents ?? null;
    if (price === null || price === suggested.current) setPrice(cents);
    suggested.current = cents;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [like.loading, like.data]);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const models: string[] = catalog.data?.models?.[f.brand] ?? [];
  const storages: string[] = catalog.data?.storages?.[`${f.brand}|${f.model}`] ?? ['64GB', '128GB', '256GB', '512GB', '1TB'];
  // The big box takes whatever is scanned: a number of about 15 digits is an IMEI (a typo in one is
  // caught); anything else (a PS5 or Switch serial, a 12-digit Xbox serial, an AirPods S/N) is saved as
  // the serial number. Blank is fine too: the item gets a stock number instead.
  const boxed = f.imei.trim();
  const boxDigits = boxed.replace(/\D/g, '');
  const boxIsImei = !!boxed && /^[\d\s-]+$/.test(boxed) && boxDigits.length >= 14 && boxDigits.length <= 16;
  const boxIsSerial = !!boxed && !boxIsImei;
  const imei = boxIsImei ? boxDigits : '';
  const serial = f.serial.trim().replace(/\s+/g, '') || (boxIsSerial ? boxed.replace(/\s+/g, '').toUpperCase() : '');
  const serialSent = serial || (imei ? '' : stockNo);
  const imeiBad = boxIsImei && !isValidImei(imei);
  const batteryBad = !!f.batteryHealth && (Number(f.batteryHealth) < 1 || Number(f.batteryHealth) > 100);
  const submit = async () => {
    const r = await run(() =>
      post('/api/devices', {
        categoryId: f.categoryId, brand: brandSent, model: modelSent, storage: f.storage || null, color: f.color || null, carrier: f.carrier, locked: f.carrier !== 'Unlocked' && f.locked,
        condition: f.condition, grade: f.condition === 'new' ? null : f.grade, imei: imei || null, imei2: f.imei2 || null, serial: serialSent || null,
        batteryHealth: f.batteryHealth ? Number(f.batteryHealth) : null, priceCents: price ?? 0, costCents: cost ?? undefined, supplierId: f.supplierId || null, notes: f.notes || null, imageUrl: photo || null,
      }),
      { success: price ? 'Added to inventory. Its price sticker is printing.' : serialSent === stockNo ? `Added to inventory as stock number ${stockNo}.` : 'Added to inventory.' },
    );
    if (r) {
      // On sale with a price: its price sticker prints right away.
      if (r.status === 'in_stock' && r.price_cents > 0) printPriceStickers(print, [r], me?.settings);
      onDone(r);
    }
  };
  return (
    <Sheet title={units.title} onClose={onClose} wide footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} onClick={submit} disabled={imeiBad || batteryBad}>Add to inventory</Button></>}>
      <div className="stack" data-enter="off" onKeyDown={enterToNextBox}>
      <div className="form-grid">
        <p className="small muted full" style={{ margin: 0 }}>Nothing here is required: fill in what you know and add it. Blanks are filled in for you (no IMEI or serial: it gets stock number {stockNo} to scan; no price: $0.00 until it's priced).</p>
        <Field label={units.phones ? 'IMEI or serial number' : 'Serial number'} hint={units.phones ? (boxIsSerial ? 'a serial number: saved as the S/N' : boxed ? 'scan the box or dial *#06#' : `optional: scan the box, dial *#06#, or type any serial; blank gets stock number ${stockNo}`) : `optional: scan or type it; blank gets stock number ${stockNo}`} className="full">
          <input className="input input-lg" autoFocus value={f.imei} onChange={set('imei')} placeholder={units.phones ? 'IMEI, serial number, or leave it blank' : 'Serial number, or leave it blank'} autoCapitalize="characters" spellCheck={false} aria-invalid={imeiBad} onKeyDown={(e) => { if (e.key !== 'Enter') return; e.preventDefault(); e.stopPropagation(); modelBox.current?.focus(); }} />
        </Field>
        {imeiBad ? <div className="error-text full">That IMEI doesn't check out. IMEIs are 15 digits; look for a typo (a serial number with letters is fine as it is).</div> : null}
        {/* Its picture comes after the IMEI box: the sheet puts the cursor in its first box, so a scan goes in the box. */}
        <div className="row full" style={{ gap: 16, alignItems: 'flex-start' }}>
          <span className="thumb" style={{ width: 96, height: 96 }}><DevicePhoto url={photo || null} brand={brandSent} model={f.model} color={f.color} category={f.categoryId} size={96} /></span>
          <div className="grow stack-sm">
            <div className="row-wrap">
              <Button icon="plus" busy={uploading} onClick={takePhoto}>Take or choose a photo</Button>
              {photo ? <Button variant="ghost" onClick={() => setPhoto('')}>Remove photo</Button> : null}
            </div>
            <span className="small muted">{photo ? 'Your photo of it.' : modelPhoto ? `The ${f.model.trim()} picture is added by itself. Take a photo to use your own.` : 'Its picture comes up by itself when you type the model (like iPhone 11), or take a photo.'}</span>
          </div>
        </div>
        <Field label="Type">
          <select className="select" value={f.categoryId} onChange={setType}>{units.cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</select>
        </Field>
        <Field label="Brand" hint={!f.brand.trim() && f.model.trim() ? `blank: saved as ${brandSent}` : 'optional'}>
          <input className="input" list="brands" value={f.brand} onChange={(e) => { setBrandTyped(true); setF({ ...f, brand: e.target.value }); }} placeholder={f.categoryId === 'consoles' ? 'e.g. Sony, Microsoft, Nintendo' : `e.g. ${units.brands.slice(0, 2).join(', ')}`} />
          <datalist id="brands">{[...new Set([...(f.categoryId === 'consoles' ? CONSOLE_BRANDS : []), ...(units.phones ? catalog.data?.brands ?? units.brands : units.brands)])].map((b: string) => <option key={b} value={b} />)}</datalist>
        </Field>
        <Field label="Model" hint={f.model.trim() ? undefined : `optional: blank is saved as "${modelSent}"`}>
          <input ref={modelBox} className="input" list="models" value={f.model} onChange={set('model')} placeholder={MODEL_HINT[f.categoryId] ?? 'e.g. iPhone 16 Pro Max'} />
          <datalist id="models">{models.map((m) => <option key={m} value={m} />)}</datalist>
        </Field>
        {units.storage ? (
          <Field label="Storage">
            <input className="input" list="storages" value={f.storage} onChange={set('storage')} placeholder="256GB" />
            <datalist id="storages">{storages.map((s) => <option key={s} value={s} />)}</datalist>
          </Field>
        ) : null}
        <Field label="Color"><input className="input" value={f.color} onChange={set('color')} placeholder={units.phones ? 'e.g. Black Titanium' : 'e.g. Silver'} /></Field>
        {units.phones ? (
          <>
            <Field label="Carrier">
              <select className="select" value={f.carrier} onChange={set('carrier')}>{CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            </Field>
            {f.carrier !== 'Unlocked' ? <label className="check"><input type="checkbox" checked={f.locked} onChange={(e) => setF({ ...f, locked: e.target.checked })} /> Locked to {f.carrier}</label> : <span />}
          </>
        ) : null}
        <Field label="Condition">
          <select className="select" value={f.condition} onChange={set('condition')}>{Object.entries(CONDITION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        </Field>
        {f.condition !== 'new' ? (
          <Field label="Grade">
            <select className="select" value={f.grade} onChange={set('grade')}>{Object.entries(GRADE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </Field>
        ) : <span />}
        <Field label="Battery health %" hint={batteryBad ? '1 to 100' : 'prints on the price sticker'}>
          <input className="input" inputMode="numeric" value={f.batteryHealth} onChange={(e) => setF({ ...f, batteryHealth: e.target.value.replace(/\D/g, '').slice(0, 3) })} placeholder="e.g. 92" />
        </Field>
        {units.phones ? <Field label="Serial number" hint={boxIsSerial ? 'from the box above' : 'optional: if the phone also has one'}><input className="input" value={f.serial} onChange={set('serial')} placeholder={boxIsSerial ? boxed.toUpperCase() : ''} /></Field> : null}
        <Field label="Selling price" hint={like.data && price === like.data.price_cents ? `the last one like it: ${like.data.title}` : price === null ? 'optional: blank is $0.00 until it’s priced' : undefined}><MoneyInput value={price} onChange={setPrice} /></Field>
        {can('inventory.view_cost') ? <Field label="Cost (what we paid)"><MoneyInput value={cost} onChange={setCost} /></Field> : <span />}
        <Field label="Supplier (optional)">
          <select className="select" value={f.supplierId} onChange={set('supplierId')}>
            <option value="">—</option>
            {(suppliers.data ?? []).map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        {units.phones ? <Field label="Second IMEI (optional)"><input className="input" inputMode="numeric" value={f.imei2} onChange={set('imei2')} /></Field> : null}
        <Field label="Notes" className="full"><textarea className="textarea" value={f.notes} onChange={set('notes')} placeholder="e.g. small scratch on the back" /></Field>
      </div>
      </div>
    </Sheet>
  );
}

function DeviceSheet({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { can, me } = useSession();
  const cart = useCart();
  const toast = useToast();
  const { run, busy } = useAction();
  const d = useLoad(() => get(`/api/devices/${id}`), [id]);
  const [price, setPrice] = useState<number | null>(null);
  const [grade, setGrade] = useState('');
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  // Its name (brand, model, storage, color) and what the store paid for it are edited here too.
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [storage, setStorage] = useState('');
  const [color, setColor] = useState('');
  const [cost, setCost] = useState<number | null>(null);
  const [removing, setRemoving] = useState(false);
  const [wholesaling, setWholesaling] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [editingSticker, setEditingSticker] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (d.data) {
      setPrice(d.data.price_cents);
      setGrade(d.data.grade ?? '');
      setStatus(d.data.status);
      setNotes(d.data.notes ?? '');
      setBrand(d.data.brand ?? '');
      setModel(d.data.model ?? '');
      setStorage(d.data.storage ?? '');
      setColor(d.data.color ?? '');
      setCost(d.data.cost_cents ?? null);
    }
  }, [d.data]);
  if (!d.data) return <Sheet title="Device" onClose={onClose}><Spinner /></Sheet>;
  const x = d.data;
  const nameChanged = brand.trim() !== x.brand || model.trim() !== x.model || storage.trim() !== (x.storage ?? '') || color.trim() !== (x.color ?? '');
  const costChanged = x.cost_cents !== null && cost !== null && cost !== x.cost_cents;
  const changed = price !== x.price_cents || grade !== (x.grade ?? '') || status !== x.status || notes !== (x.notes ?? '') || nameChanged || costChanged;
  // The brand and model can't be blank (the rest can).
  const nameOk = !!brand.trim() && !!model.trim();
  const save = async () => {
    const body: any = {};
    if (price !== x.price_cents) body.priceCents = price;
    if (brand.trim() !== x.brand) body.brand = brand.trim();
    if (model.trim() !== x.model) body.model = model.trim();
    if (storage.trim() !== (x.storage ?? '')) body.storage = storage.trim() || null;
    if (color.trim() !== (x.color ?? '')) body.color = color.trim() || null;
    if (costChanged) body.costCents = cost;
    if (grade !== (x.grade ?? '')) body.grade = grade || null;
    if (status !== x.status) body.status = status;
    if (notes !== (x.notes ?? '')) body.notes = notes;
    const r = await run(() => patch(`/api/devices/${id}`, body), { success: 'Saved.' });
    if (r) { d.reload(); onChanged(); }
  };
  const sell = () => {
    if (cart.addDevice(x)) toast(`Added ${x.title} to the sale.`, 'good');
    navigate('/sale');
  };
  const endHold = async () => {
    const r = await run(() => patch(`/api/devices/${id}`, { holdUntil: null }), { success: 'It can be sold now.' });
    if (r) { d.reload(); onChanged(); }
  };
  // The store's own photo of this one, saved right away; without one, its model's picture shows.
  const savePhoto = async (url: string | null) => {
    const r = await run(() => patch(`/api/devices/${id}`, { imageUrl: url }), { success: url ? 'Photo saved.' : 'Photo removed. The picture of its model shows again.' });
    if (r) { d.reload(); onChanged(); }
  };
  const takePhoto = async () => {
    const file = await pickPhoto({ camera: true });
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await photoToDataUrl(file, 1000, 0.85);
      const res = await post('/api/images', { dataUrl });
      await savePhoto(res.url);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That picture could not be saved.', 'error');
    } finally {
      setUploading(false);
    }
  };
  const editable = x.status !== 'sold';
  const deletable = can('inventory.adjust') && !['sold', 'removed', 'in_transit'].includes(x.status);
  if (removing) return <DeleteStockSheet title={x.title} path={`/api/devices/${id}/remove`} onClose={() => setRemoving(false)} onDone={() => { setRemoving(false); d.reload(); onChanged(); }} />;
  if (wholesaling) return <WholesaleSheet deviceId={id} onClose={() => setWholesaling(false)} onDone={() => { setWholesaling(false); d.reload(); onChanged(); }} />;
  if (undoing && x.wholesale) return <UndoWholesaleSheet deviceId={id} title={x.title} w={x.wholesale} onClose={() => setUndoing(false)} onDone={() => { setUndoing(false); d.reload(); onChanged(); }} />;
  const wholesale = () => setWholesaling(true);
  const inTransit = x.status === 'in_transit';
  return (
    <Sheet
      title={x.title}
      onClose={onClose}
      wide
      footer={
        <>
          {me?.register && x.status === 'in_stock' && !x.held && x.location_id === me.location?.id ? <Button icon="bag" onClick={sell}>Sell this</Button> : null}
          {WHOLESALE_STATUSES.includes(x.status) && x.status !== 'testing' ? <Button icon="truck" onClick={wholesale}>Wholesale</Button> : null}
          {deletable ? <Button variant="danger" icon="trash" onClick={() => setRemoving(true)}>Delete</Button> : null}
          {(x.status === 'in_stock' || x.status === 'on_hold') && x.price_cents > 0 ? (
            <Button icon="tag" disabled={changed} title={changed ? 'Save the changes first' : 'Edit and print it for the back of the phone (3 × 2 in label)'} onClick={() => setEditingSticker(true)}>Price sticker</Button>
          ) : null}
          <span className="grow" />
          <Button onClick={onClose}>Close</Button>
          {editable ? <Button variant="primary" busy={busy} disabled={!changed || !nameOk} onClick={save}>Save changes</Button> : null}
        </>
      }
    >
      <div className="row" style={{ gap: 16, alignItems: 'flex-start' }}>
        <span className="thumb" style={{ width: 120, height: 120 }}><DevicePhoto url={x.image_url} brand={x.brand} model={x.model} color={x.color} category={x.category_id} size={120} /></span>
        {editable && can('device.add') ? (
          <div className="grow stack-sm">
            <div className="row-wrap">
              <Button icon="plus" busy={uploading} disabled={busy} onClick={takePhoto}>{x.image_url ? 'Take a new photo' : 'Take or choose a photo'}</Button>
              {x.image_url ? <Button variant="ghost" disabled={busy || uploading} onClick={() => savePhoto(null)}>Remove photo</Button> : null}
            </div>
            <span className="small muted">{x.image_url ? 'The store’s own photo of this one.' : 'The picture of its model, until you take a photo of this one.'}</span>
          </div>
        ) : null}
      </div>
      <div className="row-wrap"><Badge tone={STATUS_TONE[x.status] ?? 'plain'}>{DEVICE_STATUS_LABELS[x.status]}</Badge><HoldBadge device={x} /><span className="muted">{x.location_name}</span>{x.sale ? <a href={`#/sales/${x.sale.id}`}>Sold on receipt #{x.sale.receipt_no}</a> : null}</div>
      <dl className="kv">
        <dt>IMEI</dt><dd className="num">{x.imei ?? '—'}{x.imei2 ? ` / ${x.imei2}` : ''}</dd>
        <dt>Serial</dt><dd>{x.serial ?? '—'}</dd>
        <dt>Carrier</dt><dd>{x.carrier}{x.locked ? ' (locked)' : ''}</dd>
        <dt>Condition</dt><dd>{CONDITION_LABELS[x.condition] ?? x.condition}</dd>
        <dt>Battery</dt><dd>{x.battery_health ? `${x.battery_health}%` : '—'}</dd>
        {x.cost_cents !== null ? <><dt>Cost and profit at this price</dt><dd><span className="pill-row"><Cost cents={cost ?? x.cost_cents} /><Profit cents={(price ?? x.price_cents) - (cost ?? x.cost_cents)} /></span></dd></> : null}
      </dl>
      <WholesaleInfo w={x.wholesale} soldWholesale={x.sold_wholesale} onUndo={x.status === 'sold' ? () => setUndoing(true) : undefined} />
      {x.status === 'testing' ? <TestingPanel device={x} onDone={() => { d.reload(); onChanged(); }} onWholesale={wholesale} /> : null}
      {x.hold_until && x.hold_until > Date.now() && !['sold', 'removed'].includes(x.status) ? (
        <div className="banner warn"><Icon.clock /><div className="grow">On hold until {formatDateTime(x.hold_until)} (the hold for bought phones, set in Settings → Buy & trade-in). It can be sold after that.</div>{can('inventory.adjust') ? <Button size="sm" busy={busy} onClick={endHold}>Sell it now</Button> : null}</div>
      ) : null}
      {editable ? (
        <div className="form-grid">
          {can('device.add') ? (
            <>
              <Field label="Brand" hint={brand.trim() ? undefined : 'needed'}><input className="input" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. Apple" /></Field>
              <Field label="Model" hint={model.trim() ? undefined : 'needed'}><input className="input" value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g. iPhone 16 Pro Max" /></Field>
              <Field label="Storage"><input className="input" value={storage} onChange={(e) => setStorage(e.target.value)} placeholder="e.g. 256GB" /></Field>
              <Field label="Color"><input className="input" value={color} onChange={(e) => setColor(e.target.value)} placeholder="e.g. Black Titanium" /></Field>
            </>
          ) : null}
          <Field label="Price">{can('device.edit_price') ? <MoneyInput value={price} onChange={setPrice} /> : <div className="input"><Money cents={x.price_cents} /></div>}</Field>
          {x.cost_cents !== null && can('inventory.edit') ? <Field label="Cost (what we paid)" hint="what the store paid for it"><MoneyInput value={cost} onChange={setCost} /></Field> : null}
          <Field label="Grade">
            <select className="select" value={grade} onChange={(e) => setGrade(e.target.value)} disabled={!can('device.add')}>
              <option value="">—</option>
              {Object.entries(GRADE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          {can('inventory.adjust') ? (
            <Field label="Status" hint={inTransit ? 'on its way to another store: it changes when the transfer is received or cancelled' : undefined}>
              <select className="select" value={status} onChange={(e) => setStatus(e.target.value)} disabled={inTransit}>
                {['in_stock', 'testing', 'on_hold', 'parts_only', 'removed'].map((s) => <option key={s} value={s}>{DEVICE_STATUS_LABELS[s]}</option>)}
                {['in_transit'].includes(x.status) ? <option value={x.status}>{DEVICE_STATUS_LABELS[x.status]}</option> : null}
              </select>
            </Field>
          ) : null}
          <Field label="Notes" className="full"><textarea className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!can('device.add')} /></Field>
        </div>
      ) : null}
      {editingSticker ? createPortal(<StickerSheet device={x} onClose={() => setEditingSticker(false)} onSaved={() => { d.reload(); onChanged(); }} />, document.body) : null}
      <h3>History</h3>
      <div className="list">
        {x.history.map((h: any) => (
          <div key={h.id} className="list-row" style={{ cursor: 'default' }}>
            <span className="main-col"><span className="t" style={{ display: 'block' }}>{REASONS[h.reason] ?? h.reason}{h.note ? ` · ${h.note}` : ''}</span><span className="s" style={{ display: 'block' }}>{formatDateTime(h.created_at)} · {h.location_name}{h.employee_name ? ` · ${h.employee_name}` : ''}</span></span>
          </div>
        ))}
      </div>
    </Sheet>
  );
}

/**
 * A phone's price sticker, edited before it prints (the tag button in the list, or Price sticker
 * on its page). The words are only for the sticker; a new price or battery health is saved to
 * the phone too, so the register charges what the sticker says.
 */
function StickerSheet({ device, onClose, onSaved }: { device: any; onClose: () => void; onSaved: () => void }) {
  const { can, me } = useSession();
  const print = usePrint();
  const { run, busy } = useAction();
  const [draft, setDraft] = useState<StickerDraft>(() => draftFromPhone(device));
  const ids = { imei: device.imei ?? null, serial: device.serial ?? null };
  const battery = draft.battery ? Number(draft.battery) : null;
  const priceChanged = draft.price !== null && draft.price !== device.price_cents;
  const batteryChanged = battery !== (device.battery_health ?? null);
  const saving = priceChanged || batteryChanged;
  const ready = !!draft.name.trim() && !!draft.price && draft.price > 0 && !batteryProblem(draft.battery);
  const go = async () => {
    if (!ready || busy) return;
    if (saving) {
      const body: Record<string, unknown> = {};
      if (priceChanged) body.priceCents = draft.price;
      if (batteryChanged) body.batteryHealth = battery;
      const what = [priceChanged ? 'price' : '', batteryChanged ? 'battery health' : ''].filter(Boolean).join(' and ');
      const r = await run(() => patch(`/api/devices/${device.id}`, body), { success: `The new ${what} is saved. The sticker is printing.` });
      if (!r) return;
      onSaved();
    }
    printPriceStickers(print, [stickerFromDraft(draft, ids)], me?.settings);
    onClose();
  };
  return (
    <Sheet
      title="Price sticker"
      wide
      onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" icon="tag" busy={busy} disabled={!ready} onClick={go}>{saving ? 'Save and print' : 'Print price sticker'}</Button></>}
    >
      <p className="small muted" style={{ margin: 0 }}>
        {device.title}. Change anything before it prints. A new price or battery health is saved to the phone too, so the register charges what the sticker says.
      </p>
      <StickerEditor draft={draft} onChange={setDraft} ids={ids} priceDisabled={!can('device.edit_price')} batteryDisabled={!can('device.add')} />
    </Sheet>
  );
}

// ---------------- products ----------------

const PRODUCT_CATS = [
  { id: '', label: 'Everything' },
  { id: 'accessories', label: 'All accessories' },
  { id: 'cases', label: 'Cases' },
  { id: 'screen_protectors', label: 'Screen protectors' },
  { id: 'chargers', label: 'Chargers' },
  { id: 'cables', label: 'Cables' },
  { id: 'power_banks', label: 'Power banks' },
  { id: 'headphones', label: 'Headphones' },
  { id: 'speakers', label: 'Speakers' },
  { id: 'adapters', label: 'Adapters' },
  { id: 'car_accessories', label: 'Car accessories' },
  { id: 'sunglasses', label: 'Sunglasses' },
  { id: 'other_accessories', label: 'Other accessories' },
  { id: 'apple', label: 'Sale menu: all Apple' },
  { id: 'apple_iphone', label: 'iPhone' },
  { id: 'apple_ipad', label: 'iPad' },
  { id: 'apple_macbook', label: 'MacBook' },
  { id: 'samsung', label: 'Sale menu: all Samsung' },
  { id: 'samsung_phones', label: 'Galaxy phones' },
  { id: 'samsung_tablets', label: 'Galaxy tablets' },
  { id: 'google_pixel', label: 'Sale menu: Pixel phones' },
  { id: 'moto_phones', label: 'Sale menu: Moto phones' },
  { id: 'wifi', label: 'Sale menu: all WiFi' },
  { id: 'wifi_hotspots', label: 'Hotspots' },
  { id: 'wifi_home', label: 'Home internet' },
  { id: 'wifi_routers', label: 'Routers & mesh' },
  { id: 'wifi_extenders', label: 'WiFi extenders' },
  { id: 'wifi_travel', label: 'Travel routers' },
  { id: 'perfumes', label: 'Perfumes' },
  { id: 'parts', label: 'All repair parts' },
  { id: 'screens', label: 'Screens' },
  { id: 'batteries', label: 'Batteries' },
  { id: 'charging_ports', label: 'Charging ports' },
  { id: 'cameras', label: 'Cameras' },
  { id: 'other_parts', label: 'Other parts' },
];

/** The parts shelves: the repair parts on their own (Inventory → Parts). */
const PART_CATS = [
  { id: 'parts', label: 'All repair parts' },
  { id: 'screens', label: 'Screens' },
  { id: 'batteries', label: 'Batteries' },
  { id: 'charging_ports', label: 'Charging ports' },
  { id: 'cameras', label: 'Cameras' },
  { id: 'other_parts', label: 'Other parts' },
];

/** The stock list on paper: what's on the shelves, what it costs (the owner's copy) and what it sells for, with the totals. */
function StockReportPaper({ title, store, rows, loc, showCost }: { title: string; store: string; rows: any[]; loc: string; showCost: boolean }) {
  const qty = (p: any) => (loc === 'all' ? p.total_qty : p.qty ?? 0) as number;
  const units = rows.reduce((s, p) => s + qty(p), 0);
  const retail = rows.reduce((s, p) => s + qty(p) * p.price_cents, 0);
  const cost = rows.reduce((s, p) => s + qty(p) * (p.cost_cents ?? 0), 0);
  return (
    <div className="dbd-print">
      <h1>{title} · {store}</h1>
      <p>{rows.length} item{rows.length === 1 ? '' : 's'} · {units} unit{units === 1 ? '' : 's'}{showCost ? ` · cost ${formatCents(cost)}` : ''} · retail {formatCents(retail)} · printed {formatDateTime(Date.now())}</p>
      <table className="table dr-table dbd-paper">
        <thead><tr><th>Item</th><th>Category</th><th className="right">In stock</th><th className="right">Price</th>{showCost ? <th className="right">Cost</th> : null}{showCost ? <th className="right">Total cost</th> : null}<th className="right">Total retail</th></tr></thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id}>
              <td>{p.name}{p.sku ? <span className="muted"> · {p.sku}</span> : null}</td>
              <td>{p.category_name}</td>
              <td className="right num">{qty(p)}</td>
              <td className="right num">{formatCents(p.price_cents)}</td>
              {showCost ? <td className="right num">{formatCents(p.cost_cents ?? 0)}</td> : null}
              {showCost ? <td className="right num">{formatCents(qty(p) * (p.cost_cents ?? 0))}</td> : null}
              <td className="right num">{formatCents(qty(p) * p.price_cents)}</td>
            </tr>
          ))}
          <tr className="dr-total"><td>Total</td><td /><td className="right num">{units}</td><td /> {showCost ? <td /> : null}{showCost ? <td className="right num">{formatCents(cost)}</td> : null}<td className="right num">{formatCents(retail)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function ProductsTab({ loc, openId, parts = false }: { loc: string; openId: string | null; parts?: boolean }) {
  const { can, me } = useSession();
  const plan = usePlan();
  const print = usePrint();
  const productCats = parts ? PART_CATS : plan.industry === 'cellphone' ? PRODUCT_CATS : shopProductCats(plan.industry);
  const [cat, setCat] = useState(parts ? 'parts' : '');
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const [open, setOpen] = useState<string | null>(openId);
  const [adding, setAdding] = useState(false);
  const [fromLink, setFromLink] = useState(false);
  const [importing, setImporting] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [prefill, setPrefill] = useState<Prefill | null>(null);
  // "In stock": only what's on the shelves. Ticked rows (or Select all) print as a report.
  const [inStock, setInStock] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  // Edit the ticked products in one go: price, cost, category, low-stock alert, supplier, taxable, brand.
  const [editingMany, setEditingMany] = useState(false);
  // One click on a row's own Edit icon opens it straight into the full editor — no picking, no opening the row first.
  const [quickEdit, setQuickEdit] = useState<any | null>(null);
  const cats = useLoad(() => get('/api/categories'), []);
  const suppliers = useLoad(() => get('/api/suppliers'), []);
  useEffect(() => setOpen(openId), [openId]);
  const list = useLoad(() => get(`/api/products${qs({ location: loc, category: cat, q: dq, limit: 1000 })}`), [loc, cat, dq]);
  const showCost = can('inventory.view_cost');
  const canEdit = can('inventory.edit');
  const bulkFields: BulkField[] = [
    { key: 'priceCents', label: 'New price', kind: 'money' },
    ...(showCost ? [{ key: 'costCents', label: 'New cost (what we pay)', kind: 'money' } as BulkField] : []),
    { key: 'categoryId', label: 'Category', kind: 'select', options: ((cats.data ?? []) as any[]).filter((c) => c.tracking === 'quantity').map((c) => ({ value: c.id, label: c.name })) },
    { key: 'minQty', label: 'Low-stock alert at', kind: 'number', hint: 'blank: unchanged · 0 turns it off' },
    { key: 'supplierId', label: 'Supplier', kind: 'select', options: [{ value: '-', label: 'No supplier' }, ...((suppliers.data ?? []) as any[]).map((x) => ({ value: x.id, label: x.name }))] },
    { key: 'taxable', label: 'Sales tax', kind: 'yesno', yes: 'Taxed', no: 'Not taxed' },
    { key: 'brand', label: 'Brand', kind: 'text', maxLength: 60 },
  ];
  const applyBulk = async (item: { id: string }, v: BulkValues) => {
    const body: Record<string, unknown> = {};
    if (typeof v.priceCents === 'number') body.priceCents = v.priceCents;
    if (typeof v.costCents === 'number') body.costCents = v.costCents;
    if (typeof v.categoryId === 'string') body.categoryId = v.categoryId;
    if (typeof v.minQty === 'number') body.minQty = v.minQty;
    if (typeof v.supplierId === 'string') body.supplierId = v.supplierId === '-' ? null : v.supplierId;
    if (typeof v.taxable === 'boolean') body.taxable = v.taxable;
    if (typeof v.brand === 'string') body.brand = v.brand.trim() || null;
    await patch(`/api/products/${item.id}`, body);
  };
  const qtyOf = (p: any) => (loc === 'all' ? p.total_qty : p.qty ?? 0) as number;
  const rows: any[] = (list.data ?? []).filter((p: any) => !inStock || qtyOf(p) > 0);
  const chosen = rows.filter((p) => picked.includes(p.id));
  const allPicked = rows.length > 0 && chosen.length === rows.length;
  const toggle = (id: string) => setPicked((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]));
  const units = rows.reduce((s, p) => s + qtyOf(p), 0);
  const storeName = loc === 'all' ? 'All stores' : me?.locations.find((l) => l.id === loc)?.name ?? me?.location?.name ?? 'this store';
  const title = parts ? 'Parts' : 'Products';
  const printReport = () => print(<StockReportPaper title={`${title} in stock`} store={storeName} rows={chosen.length ? chosen : rows} loc={loc} showCost={showCost} />, 'letter');
  return (
    <section className="stack">
      <div className="row-wrap">
        <div className="search grow" style={{ minWidth: 240 }}>
          <Icon.search />
          <input className="input" placeholder={parts ? 'Part name, SKU or barcode' : 'Name, SKU or barcode'} value={q} onChange={(e) => setQ(e.target.value)} aria-label={parts ? 'Search parts' : 'Search products'} />
        </div>
        <select className="select" style={{ width: 'auto' }} value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category">
          {productCats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        {can('inventory.receive') ? <Button icon="scan" onClick={() => setReceiving(true)}>Receive delivery</Button> : null}
        {can('inventory.edit') ? <Button icon="tag" onClick={() => setFromLink(true)}>Add from supplier link</Button> : null}
        {can('inventory.edit') ? <Button icon="list" onClick={() => setImporting(true)}>Import a list</Button> : null}
        {can('inventory.edit') ? <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>{parts ? 'Add a part' : 'Add product'}</Button> : null}
      </div>
      <div className="row-wrap">
        <button type="button" className="chip" aria-pressed={inStock} onClick={() => { setInStock(!inStock); setPicked([]); }}><Icon.boxes /> In stock</button>
        {rows.length ? (
          <label className="chip" style={{ cursor: 'pointer' }}>
            <input type="checkbox" className="row-pick" checked={allPicked} onChange={() => setPicked(allPicked ? [] : rows.map((p) => p.id))} aria-label="Select all" /> Select all
          </label>
        ) : null}
        <Button icon="printer" disabled={!rows.length} onClick={printReport}>{chosen.length ? `Print report (${chosen.length})` : 'Print report'}</Button>
        {canEdit && chosen.length ? <Button icon="edit" onClick={() => setEditingMany(true)}>Edit {chosen.length}</Button> : null}
        <span className="small muted grow">{list.data ? `${rows.length} item${rows.length === 1 ? '' : 's'} · ${units} unit${units === 1 ? '' : 's'}${chosen.length ? ` · ${chosen.length} picked` : ''}` : ''}</span>
        {chosen.length ? <Button size="sm" variant="ghost" onClick={() => setPicked([])}>Clear</Button> : null}
      </div>
      {list.loading && !list.data ? <Spinner /> : null}
      {list.data ? (
        rows.length ? (
          <div className="list">
            <table className="table">
              <thead><tr><th style={{ width: 36 }}><input type="checkbox" className="row-pick" checked={allPicked} onChange={() => setPicked(allPicked ? [] : rows.map((p) => p.id))} aria-label="Select all rows" /></th><th style={{ width: 64 }} /><th>{parts ? 'Part' : 'Product'}</th><th>Category</th><th className="right">{loc === 'all' ? 'All stores' : 'In stock'}</th>{loc !== 'all' ? <th className="right">All stores</th> : null}<th className="right">Price</th>{showCost ? <th className="right">Cost</th> : null}{showCost ? <th className="right">Profit</th> : null}{canEdit ? <th style={{ width: 44 }} /> : null}</tr></thead>
              <tbody>
                {rows.map((p: any) => (
                  <tr key={p.id} className="click" onClick={() => setOpen(p.id)}>
                    <td onClick={(e) => e.stopPropagation()}><input type="checkbox" className="row-pick" checked={picked.includes(p.id)} onChange={() => toggle(p.id)} aria-label={`Pick ${p.name}`} /></td>
                    <td><span className="thumb" style={{ width: 48, height: 48 }}><ProductPicture url={p.image_url} categoryId={p.category_id} name={p.name} size={48} /></span></td>
                    <td><div className="strong">{p.name}</div><div className="small muted">{[p.brand, p.sku, p.barcode].filter(Boolean).join(' · ')}</div></td>
                    <td className="small">{p.category_name}</td>
                    <td className="right">{loc === 'all' ? p.total_qty : p.qty ?? 0} {p.low ? <Badge tone="amber" icon="alert">Low</Badge> : null}</td>
                    {loc !== 'all' ? <td className="right muted">{p.total_qty}</td> : null}
                    <td className="right strong"><Money cents={p.price_cents} /></td>
                    {showCost ? <td className="right"><Cost cents={p.cost_cents} label={false} /></td> : null}
                    {showCost ? <td className="right"><Profit cents={p.profit_cents} label={false} /></td> : null}
                    {canEdit ? (
                      <td onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" icon="edit" aria-label={`Edit ${p.name}`} title="Edit" onClick={() => setQuickEdit(p)} />
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty icon="boxes" title={inStock ? (parts ? 'No parts in stock here.' : 'Nothing in stock here.') : parts ? 'No parts match.' : 'No products match.'} />
        )
      ) : null}
      {adding ? <ProductForm prefill={prefill} defaultCategoryId={parts ? 'screens' : undefined} onClose={() => { setAdding(false); setPrefill(null); }} onDone={() => { setAdding(false); setPrefill(null); list.reload(); }} /> : null}
      {quickEdit ? <ProductForm product={quickEdit} onClose={() => setQuickEdit(null)} onDone={() => { setQuickEdit(null); list.reload(); }} /> : null}
      {editingMany ? <BulkEditSheet title={parts ? 'Edit parts' : 'Edit products'} what={parts ? 'parts' : 'products'} items={chosen.map((p) => ({ id: p.id, label: `${p.name} · ${formatCents(p.price_cents)}` }))} fields={bulkFields} apply={applyBulk} onClose={() => setEditingMany(false)} onDone={() => { setEditingMany(false); setPicked([]); list.reload(); }} /> : null}
      {open ? <ProductSheet id={open} onClose={() => { setOpen(null); if (openId) navigate('/inventory/products'); }} onChanged={list.reload} /> : null}
      {fromLink ? <SupplierLinkSheet onClose={() => setFromLink(false)} onFound={(x) => { setFromLink(false); setPrefill(x); setAdding(true); }} /> : null}
      {importing ? <ImportSheet onClose={() => setImporting(false)} onDone={() => { setImporting(false); list.reload(); }} /> : null}
      {receiving ? <ReceiveSheet onClose={() => setReceiving(false)} onDone={() => { setReceiving(false); list.reload(); }} /> : null}
    </section>
  );
}

interface Prefill { name: string | null; imageUrl: string | null; costCents: number | null; sku: string | null; barcode: string | null; url: string }

function guessPartCategory(name: string): string {
  if (/screen|lcd|oled|display/i.test(name)) return 'screens';
  if (/batter/i.test(name)) return 'batteries';
  if (/charging port|charge port|dock/i.test(name)) return 'charging_ports';
  if (/camera|lens/i.test(name)) return 'cameras';
  if (/case|cover/i.test(name)) return 'cases';
  return 'other_parts';
}

function ProductForm({ product, prefill, defaultCategoryId, onClose, onDone }: { product?: any; prefill?: Prefill | null; defaultCategoryId?: string; onClose: () => void; onDone: () => void }) {
  const { can } = useSession();
  const toast = useToast();
  const { run, busy } = useAction();
  const guard = useScanGuard();
  const cats = useLoad(() => get('/api/categories'), []);
  const suppliers = useLoad(() => get('/api/suppliers'), []);
  const [f, setF] = useState({
    name: product?.name ?? prefill?.name ?? '', categoryId: product?.category_id ?? (prefill ? guessPartCategory(prefill.name ?? '') : defaultCategoryId ?? 'cases'), brand: product?.brand ?? '',
    sku: product?.sku ?? prefill?.sku ?? '', barcode: product?.barcode ?? prefill?.barcode ?? '',
    minQty: String(product?.min_qty ?? 3), supplierId: product?.supplier_id ?? '', size: product?.attrs?.size ?? '', fits: product?.attrs?.fits ?? '', taxable: product?.taxable ?? true, initialQty: '',
  });
  // A barcode scanned into a number box goes back out (into the Barcode box when it's empty).
  const scannedInto = (k: 'minQty' | 'initialQty') => (code: string, before: string) => {
    setF((x) => ({ ...x, [k]: before, barcode: x.barcode || code }));
    toast(f.barcode ? 'That was a barcode scan, not a number.' : 'That was a barcode: it’s in the Barcode box now.', 'error');
  };
  // A barcode scanned into the name goes to the Barcode box; the name stays as it was.
  const scannedName = (code: string, before: string) => {
    setF((x) => ({ ...x, name: before, barcode: x.barcode || code }));
    toast('That was a barcode: it’s in the Barcode box now. Type the product’s name.', 'info');
  };
  const digits = (k: 'minQty' | 'initialQty') => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value.replace(/\D/g, '').slice(0, 5) });
  const [price, setPrice] = useState<number | null>(product?.price_cents ?? null);
  const [cost, setCost] = useState<number | null>(product?.cost_cents ?? prefill?.costCents ?? null);
  const [image, setImage] = useState<string>(product?.image_url ?? prefill?.imageUrl ?? '');
  // A picture comes up by itself from the name (a white USB-C charger for "Charger Apple Type C",
  // a case for "iPhone 13 case", the phone for "iPhone 11 64GB") until a photo is taken or a link
  // pasted. Only when adding, and not once it's removed.
  const [autoPic, setAutoPic] = useState(!product && !prefill?.imageUrl);
  const picName = useDebounced(f.name.trim(), 400);
  const suggested = useLoad<{ url: string | null; from: string | null }>(
    () => (autoPic && picName.length >= 3 ? get(`/api/photos/suggest${qs({ name: picName, category: f.categoryId })}`) : Promise.resolve({ url: null, from: null })),
    [autoPic, picName, f.categoryId],
  );
  const autoUrl = autoPic && !image ? suggested.data?.url ?? null : null;
  const [uploading, setUploading] = useState(false);
  const takePhoto = async () => {
    const file = await pickPhoto({ camera: true });
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await photoToDataUrl(file, 1000, 0.85);
      const res = await post('/api/images', { dataUrl });
      setImage(res.url);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That picture could not be saved.', 'error');
    } finally {
      setUploading(false);
    }
  };
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const qtyCats = (cats.data ?? []).filter((c: any) => c.tracking === 'quantity');
  const isPerfume = f.categoryId === 'perfumes';
  const isPart = ['parts', 'screens', 'batteries', 'charging_ports', 'cameras', 'other_parts'].includes(f.categoryId);
  const submit = async () => {
    const attrs: Record<string, string> = { ...(product?.attrs ?? {}) };
    if (f.size) attrs.size = f.size; else delete attrs.size;
    if (f.fits) attrs.fits = f.fits; else delete attrs.fits;
    if (prefill?.url) attrs.supplier_link = prefill.url;
    const body: any = {
      name: f.name, categoryId: f.categoryId, brand: f.brand || null, sku: f.sku || null, barcode: f.barcode || null, priceCents: price ?? 0,
      minQty: Number(f.minQty || 0), supplierId: f.supplierId || null, attrs, taxable: !!f.taxable, imageUrl: image.trim() || autoUrl || null,
    };
    if (can('inventory.view_cost') && cost !== null) body.costCents = cost;
    if (!product && f.initialQty) body.initialQty = Number(f.initialQty);
    const r = await run(() => (product ? patch(`/api/products/${product.id}`, body) : post('/api/products', body)), { success: product ? 'Saved.' : 'Product added.' });
    if (r) onDone();
  };
  return (
    <Sheet title={product ? `Edit ${product.name}` : 'Add a product'} onClose={onClose} wide footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} onClick={submit} disabled={!f.name.trim()}>{product ? 'Save' : 'Add product'}</Button></>}>
      <div className="stack" data-enter="off" onKeyDown={enterToNextBox}>
      <div className="form-grid">
        <Field label="Product name" className="full"><input className="input" autoFocus value={f.name} onChange={set('name')} onKeyDown={(e) => guard(e, scannedName)} placeholder={isPerfume ? 'e.g. Sauvage Eau de Toilette 100 ml' : 'e.g. iPhone 17 Pro Max Clear Case'} /></Field>
        {/* The picture after the name: the name box is first, so it has the cursor (and a scan in it goes to Barcode). */}
        <div className="row full" style={{ gap: 16, alignItems: 'flex-start' }}>
          <span className="thumb" style={{ width: 96, height: 96 }}><ProductPicture url={image || autoUrl} categoryId={f.categoryId} name={f.name || 'Product'} size={96} /></span>
          <div className="grow stack-sm">
            <div className="row-wrap">
              <Button icon="plus" busy={uploading} onClick={takePhoto}>Take or choose a photo</Button>
              {image || autoUrl ? <Button variant="ghost" onClick={() => { setImage(''); setAutoPic(false); }}>Remove picture</Button> : null}
              {autoUrl ? <span className="small muted">Picked by itself from the name{suggested.data?.from ? ` (${suggested.data.from})` : ''}. Take a photo or paste a link to use your own.</span> : null}
            </div>
            <Field label="Or paste a picture link" hint="https://…"><input className="input" value={image.startsWith('/api/') ? '' : image} onChange={(e) => setImage(e.target.value)} placeholder={image.startsWith('/api/') ? 'Photo saved' : 'https://'} /></Field>
          </div>
        </div>
        <Field label="Category"><select className="select" value={f.categoryId} onChange={set('categoryId')}>{qtyCats.map((c: any) => <option key={c.id} value={c.id}>{c.parent_id ? `  ${c.name}` : c.name}</option>)}</select></Field>
        <Field label="Brand"><input className="input" value={f.brand} onChange={set('brand')} /></Field>
        <Field label="Barcode" hint="scan it"><input className="input" data-enter="off" value={f.barcode} onChange={set('barcode')} /></Field>
        <Field label="SKU"><input className="input" value={f.sku} onChange={set('sku')} /></Field>
        <Field label="Selling price" hint={price === null ? 'optional: blank is $0.00 until it’s priced' : undefined}><MoneyInput value={price} onChange={setPrice} /></Field>
        {can('inventory.view_cost') ? <Field label="Cost"><MoneyInput value={cost} onChange={setCost} /></Field> : <span />}
        {isPerfume ? <Field label="Size"><input className="input" value={f.size} onChange={set('size')} placeholder="100 ml" /></Field> : null}
        {isPart ? <Field label="Fits model"><input className="input" value={f.fits} onChange={set('fits')} placeholder="iPhone 15 Pro Max" /></Field> : null}
        <Field label="Low-stock alert at" hint="0 = no alert"><input className="input" inputMode="numeric" data-enter="off" value={f.minQty} onChange={digits('minQty')} onKeyDown={(e) => guard(e, scannedInto('minQty'))} /></Field>
        <Field label="Supplier">
          <select className="select" value={f.supplierId} onChange={set('supplierId')}>
            <option value="">—</option>
            {(suppliers.data ?? []).map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        {!product ? (
          <Field label="How many do you have here now?" hint={Number(f.initialQty) > MAX_LINE_QTY ? 'up to 10,000' : undefined}>
            <input className="input" inputMode="numeric" data-enter="off" value={f.initialQty} onChange={digits('initialQty')} onKeyDown={(e) => guard(e, scannedInto('initialQty'))} placeholder="0" />
          </Field>
        ) : null}
        <label className="check"><input type="checkbox" checked={!!f.taxable} onChange={(e) => setF({ ...f, taxable: e.target.checked })} /> Charge sales tax</label>
      </div>
      </div>
    </Sheet>
  );
}

function ProductSheet({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { can, me } = useSession();
  const cart = useCart();
  const toast = useToast();
  const p = useLoad(() => get(`/api/products/${id}`), [id]);
  const [mode, setMode] = useState<null | 'receive' | 'adjust' | 'edit' | 'delete'>(null);
  if (!p.data) return <Sheet title="Product" onClose={onClose}><Spinner /></Sheet>;
  const x = p.data;
  const done = () => { setMode(null); p.reload(); onChanged(); };
  const here = Number(x.by_location?.find((l: any) => l.location_id === me?.location?.id)?.qty ?? x.qty ?? 0);
  if (mode === 'delete') return <DeleteStockSheet title={x.name} max={here} path={`/api/products/${x.id}/remove`} onClose={() => setMode(null)} onDone={done} />;
  if (mode === 'edit') return <ProductForm product={x} onClose={() => setMode(null)} onDone={done} />;
  if (mode === 'receive') return <ReceiveSheet first={x} onClose={() => setMode(null)} onDone={done} />;
  if (mode === 'adjust') return <CountSheet product={x} onClose={() => setMode(null)} onDone={done} />;
  return (
    <Sheet
      title={x.name}
      onClose={onClose}
      wide
      footer={
        <>
          {me?.register ? <Button icon="bag" onClick={() => { cart.addProduct(x); toast(`Added ${x.name} to the sale.`, 'good'); navigate('/sale'); }}>Sell this</Button> : null}
          <span className="grow" />
          {can('inventory.receive') ? <Button icon="truck" onClick={() => setMode('receive')}>Receive delivery</Button> : null}
          {can('inventory.adjust') && here > 0 ? <Button variant="danger" icon="trash" onClick={() => setMode('delete')}>Delete</Button> : null}
          {can('inventory.adjust') ? <Button icon="edit" onClick={() => setMode('adjust')}>Correct count</Button> : null}
          {can('inventory.edit') ? <Button variant="primary" icon="edit" onClick={() => setMode('edit')}>Edit</Button> : null}
        </>
      }
    >
      <div className="row" style={{ gap: 16 }}>
        <span className="thumb" style={{ width: 120, height: 120 }}><ProductPicture url={x.image_url} categoryId={x.category_id} name={x.name} size={120} /></span>
        <div className="grow small muted">{x.attrs?.supplier_link ? <a href={x.attrs.supplier_link} target="_blank" rel="noreferrer">Open at the supplier</a> : null}</div>
      </div>
      <div className="stat-row">
        <div className="stat"><div className="k">Price</div><div className="v">{formatCents(x.price_cents)}</div></div>
        {x.cost_cents !== null ? <div className="stat stat-cost"><div className="k">Cost</div><div className="v">{formatCents(x.cost_cents)}</div></div> : null}
        {x.profit_cents !== null ? <div className={`stat ${statTone('profit', x.profit_cents)}`}><div className="k">{x.profit_cents < 0 ? 'Loss each' : 'Profit each'}</div><div className="v">{formatCents(Math.abs(x.profit_cents))}</div></div> : null}
        <div className="stat"><div className="k">Low-stock alert at</div><div className="v">{x.min_qty || 'Off'}</div></div>
      </div>
      <dl className="kv">
        <dt>Category</dt><dd>{x.category_name}</dd>
        <dt>SKU / barcode</dt><dd>{[x.sku, x.barcode].filter(Boolean).join(' · ') || '—'}</dd>
        {x.attrs?.size ? <><dt>Size</dt><dd>{x.attrs.size}</dd></> : null}
        {x.attrs?.fits ? <><dt>Fits</dt><dd>{x.attrs.fits}</dd></> : null}
        <dt>Supplier</dt><dd>{x.supplier_name ?? '—'}</dd>
      </dl>
      <h3>Stock by store</h3>
      <div className="stat-row">
        {x.by_location.map((l: any) => (
          <div key={l.location_id} className="stat" style={{ background: x.min_qty && l.qty <= x.min_qty ? 'var(--amber-soft)' : undefined }}>
            <div className="k">{l.location_name}</div><div className="v">{l.qty}</div>
          </div>
        ))}
      </div>
      <h3>Recent changes</h3>
      <div className="list">
        {x.movements.length ? x.movements.map((m: any) => (
          <div key={m.id} className="list-row" style={{ cursor: 'default' }}>
            <span className="main-col"><span className="t" style={{ display: 'block' }}>{REASONS[m.reason] ?? m.reason}{m.note ? ` · ${m.note}` : ''}</span><span className="s" style={{ display: 'block' }}>{formatDateTime(m.created_at)} · {m.location_name}{m.employee_name ? ` · ${m.employee_name}` : ''}</span></span>
            <span className={`strong num ${m.qty_change < 0 ? '' : ''}`}>{m.qty_change > 0 ? `+${m.qty_change}` : m.qty_change}</span>
          </div>
        )) : <div className="empty">No changes yet.</div>}
      </div>
    </Sheet>
  );
}

const REMOVE_REASONS = ['Damaged', 'Lost or stolen', 'Returned to the supplier', 'Used for a repair', 'Store use', 'Count was wrong'];

/**
 * Delete from stock: a phone, or some accessories, leave inventory without a sale. Only managers
 * and the owner see the button; the reason and who did it stay in the history.
 */
function DeleteStockSheet({ title, max, path, onClose, onDone }: { title: string; max?: number; path: string; onClose: () => void; onDone: () => void }) {
  const { run, busy } = useAction();
  const toast = useToast();
  const guard = useScanGuard();
  const [why, setWhy] = useState('');
  const [note, setNote] = useState('');
  const [qty, setQty] = useState('1');
  const reason = [why, note.trim()].filter(Boolean).join(': ');
  const n = Number(qty);
  const qtyOk = max === undefined || (Number.isInteger(n) && n >= 1 && n <= max);
  const go = async () => {
    const r = await run(() => post(path, max === undefined ? { reason } : { qty: n, reason }), { success: 'Deleted from stock.' });
    if (r) onDone();
  };
  return (
    <Sheet
      title="Delete from stock"
      onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="danger" icon="trash" busy={busy} disabled={!reason || !qtyOk} onClick={go}>Delete from stock</Button></>}
    >
      <p>Takes <strong>{title}</strong> out of stock without a sale. Only managers and the owner can do this; your name and the reason stay in its history.</p>
      {max !== undefined ? (
        <Field label={`How many? (${max} in stock at this store)`}>
          <input
            className="input input-lg" inputMode="numeric" data-enter="off" value={qty} onChange={(e) => setQty(e.target.value.replace(/\D/g, '').slice(0, 5))}
            onKeyDown={(e) => guard(e, (_code, before) => { setQty(before); toast('That was a barcode scan, not a number. Type how many.', 'error'); })}
          />
        </Field>
      ) : null}
      <div className="stack-sm">
        <span className="small strong">Why?</span>
        <div className="chips" role="group" aria-label="Why">{REMOVE_REASONS.map((r) => <button key={r} type="button" className="chip" aria-pressed={why === r} onClick={() => setWhy(why === r ? '' : r)}>{r}</button>)}</div>
      </div>
      <Field label={why ? 'Note (optional)' : 'Or write the reason'}>
        <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. dropped, screen cracked" />
      </Field>
    </Sheet>
  );
}

/** Delete from stock, several phones at once: one reason for all of them, taken out one by one. */
function RemoveManySheet({ devices, onClose, onDone }: { devices: any[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [why, setWhy] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const reason = [why, note.trim()].filter(Boolean).join(': ');
  const go = async () => {
    setBusy(true);
    let ok = 0;
    const failed: string[] = [];
    for (const d of devices) {
      try {
        await post(`/api/devices/${d.id}/remove`, { reason });
        ok++;
      } catch (e) {
        failed.push(`${d.title}: ${e instanceof Error ? e.message : 'didn’t come out'}`);
      }
      setDone(ok + failed.length);
    }
    setBusy(false);
    if (failed.length) toast(`${ok} removed. ${failed.length} didn’t come out: ${failed.slice(0, 3).join('; ')}${failed.length > 3 ? '…' : ''}`, 'error');
    else toast(`${ok} phone${ok === 1 ? '' : 's'} removed from inventory.`, 'good');
    onDone();
  };
  return (
    <Sheet
      title={`Remove ${devices.length} from inventory`}
      onClose={onClose}
      footer={<><Button onClick={onClose} disabled={busy}>Cancel</Button><Button variant="danger" icon="trash" disabled={!reason || busy} onClick={go}>{busy ? `Removing ${done} of ${devices.length}…` : `Remove ${devices.length} from inventory`}</Button></>}
    >
      <p>Takes these <strong>{devices.length}</strong> phones out of stock without a sale. Only managers and the owner can do this; your name and the reason stay in each phone’s history.</p>
      <ul className="small" style={{ maxHeight: 160, overflow: 'auto', margin: 0, paddingLeft: 20 }}>{devices.map((d) => <li key={d.id}>{d.title}</li>)}</ul>
      <div className="stack-sm">
        <span className="small strong">Why?</span>
        <div className="chips" role="group" aria-label="Why">{REMOVE_REASONS.map((r) => <button key={r} type="button" className="chip" aria-pressed={why === r} onClick={() => setWhy(why === r ? '' : r)}>{r}</button>)}</div>
      </div>
      <Field label={why ? 'Note (optional)' : 'Or write the reason'}>
        <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. old stock, sold elsewhere" />
      </Field>
    </Sheet>
  );
}

/** Edit several phones at once: a new price, status or grade for all of them. Blank means unchanged. */
/** Edit several phones at once (price, cost, status, grade). `initial.status` starts the sheet on a status (Add to inventory: In stock). Also used from the goals bar lists. */
export function EditManySheet({ devices, initial, onClose, onDone }: { devices: any[]; initial?: { status?: string }; onClose: () => void; onDone: () => void }) {
  const { can } = useSession();
  const toast = useToast();
  const [price, setPrice] = useState<number | null>(null);
  const [cost, setCost] = useState<number | null>(null);
  const [status, setStatus] = useState(initial?.status ?? '');
  const [grade, setGrade] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const editCost = can('inventory.edit') && can('inventory.view_cost');
  const body: Record<string, unknown> = {};
  if (price !== null && can('device.edit_price')) body.priceCents = price;
  if (cost !== null && editCost) body.costCents = cost;
  if (status && can('inventory.adjust')) body.status = status;
  if (grade && can('device.add')) body.grade = grade;
  const anything = Object.keys(body).length > 0;
  const go = async () => {
    setBusy(true);
    let ok = 0;
    const failed: string[] = [];
    for (const d of devices) {
      try {
        await patch(`/api/devices/${d.id}`, body);
        ok++;
      } catch (e) {
        failed.push(`${d.title}: ${e instanceof Error ? e.message : 'didn’t save'}`);
      }
      setDone(ok + failed.length);
    }
    setBusy(false);
    if (failed.length) toast(`${ok} saved. ${failed.length} didn’t: ${failed.slice(0, 3).join('; ')}${failed.length > 3 ? '…' : ''}`, 'error');
    else toast(`${ok} phone${ok === 1 ? '' : 's'} updated.`, 'good');
    onDone();
  };
  return (
    <Sheet
      title={`Edit ${devices.length} phone${devices.length === 1 ? '' : 's'}`}
      onClose={onClose}
      footer={<><Button onClick={onClose} disabled={busy}>Cancel</Button><Button variant="primary" icon="check" disabled={!anything || busy} onClick={go}>{busy ? `Saving ${done} of ${devices.length}…` : `Save ${devices.length}`}</Button></>}
    >
      <p className="muted">Fill in only what should change on all {devices.length}; everything else stays as it is.</p>
      <ul className="small" style={{ maxHeight: 160, overflow: 'auto', margin: 0, paddingLeft: 20 }}>{devices.map((d) => <li key={d.id}>{d.title} · {formatCents(d.price_cents)}</li>)}</ul>
      <div className="form-grid">
        {can('device.edit_price') ? <Field label="New price" hint="blank: unchanged"><MoneyInput value={price} onChange={setPrice} /></Field> : null}
        {editCost ? <Field label="New cost (what we paid)" hint="blank: unchanged"><MoneyInput value={cost} onChange={setCost} /></Field> : null}
        {can('inventory.adjust') ? (
          <Field label="Status">
            <select className="select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Unchanged</option>
              {['in_stock', 'on_hold', 'testing', 'parts_only'].map((s) => <option key={s} value={s}>{DEVICE_STATUS_LABELS[s]}</option>)}
            </select>
          </Field>
        ) : null}
        {can('device.add') ? (
          <Field label="Grade">
            <select className="select" value={grade} onChange={(e) => setGrade(e.target.value)}>
              <option value="">Unchanged</option>
              {Object.entries(GRADE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
        ) : null}
      </div>
    </Sheet>
  );
}

/** Correct the count: what's actually on the shelf here. A barcode scanned into the box isn't a count. */
function CountSheet({ product, onClose, onDone }: { product: any; onClose: () => void; onDone: () => void }) {
  const { me } = useSession();
  const toast = useToast();
  const { run, busy } = useAction();
  const guard = useScanGuard();
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const tooMany = qty !== '' && Number(qty) > MAX_LINE_QTY;
  const go = async () => {
    const r = await run(() => post(`/api/products/${product.id}/adjust`, { newQty: Number(qty), reason }), { success: 'Stock updated.' });
    if (r) onDone();
  };
  return (
    <Sheet
      title="Correct the count"
      onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} onClick={go} disabled={qty === '' || tooMany || !reason.trim()}>Save</Button></>}
    >
      <p className="muted">{me?.location?.name} has {product.qty ?? 0} now.</p>
      <Field label="How many are actually on the shelf?" hint={tooMany ? 'up to 10,000' : undefined}>
        <input
          className="input input-lg" inputMode="numeric" autoFocus data-enter="off" value={qty} aria-invalid={tooMany}
          onChange={(e) => setQty(e.target.value.replace(/\D/g, '').slice(0, 5))}
          onKeyDown={(e) => guard(e, (_code, before) => { setQty(before); toast('That was a barcode scan, not a count. Type how many are on the shelf.', 'error'); })}
        />
      </Field>
      <Field label="Reason">
        <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Recount, damaged, missing" />
      </Field>
    </Sheet>
  );
}

interface ReceiveLine { id: string; name: string; codes: string; here: number; qty: number; cost: number | null; costWas: number | null }

const receiveLine = (p: any, qty: number): ReceiveLine => ({
  id: p.id, name: p.name, codes: [p.sku, p.barcode].filter(Boolean).join(' · '), here: Number(p.qty ?? 0), qty, cost: p.cost_cents ?? null, costWas: p.cost_cents ?? null,
});

/**
 * Receive delivery: scan each item's barcode (every scan adds one), or search by name, fix the
 * counts with − and +, then save it all at once. Nothing in this window saves on Enter: a scanner
 * presses Enter after every barcode, and a barcode scanned into a count box counts as a scan.
 */
function ReceiveSheet({ first, onClose, onDone }: { first?: any; onClose: () => void; onDone: () => void }) {
  const { can, me } = useSession();
  const toast = useToast();
  const { run, busy } = useAction();
  const guard = useScanGuard();
  const [lines, setLines] = useState<ReceiveLine[]>(() => (first ? [receiveLine(first, 0)] : []));
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim(), 200);
  const [note, setNote] = useState('');
  const [lastId, setLastId] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  // Opened from an item's page: the cursor starts in its count, to type how many came in (a scan
  // there still counts as a scan). Runs after the window puts the cursor in its first box.
  const firstQty = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (first) firstQty.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const hits = useLoad<any[]>(() => (dq.length >= 2 ? get(`/api/products${qs({ q: dq, limit: 8 })}`) : Promise.resolve([])), [dq]);
  const editCost = can('inventory.edit') && can('inventory.view_cost');
  const units = lines.reduce((s, l) => s + l.qty, 0);
  const tooMany = lines.some((l) => l.qty > MAX_LINE_QTY);
  const setQty = (id: string, qty: number) => setLines((ls) => ls.map((l) => (l.id === id ? { ...l, qty: Math.max(0, qty) } : l)));
  const add = (p: any) => {
    setLines((ls) => (ls.some((l) => l.id === p.id) ? ls.map((l) => (l.id === p.id ? { ...l, qty: l.qty + 1 } : l)) : [...ls, receiveLine(p, 1)]));
    setLastId(p.id);
  };
  // A barcode or SKU, scanned or typed: one more of that item.
  const addCode = async (raw: string) => {
    const code = raw.trim();
    if (!code) return;
    try {
      const r = await get<{ result: any }>(`/api/lookup${qs({ code })}`);
      if (r.result?.type === 'product') return add(r.result.product);
      if (r.result?.type === 'device') return toast('That’s a phone. Phones are added one at a time with Add phone or device.', 'error');
      // A name typed with one item matching it.
      const one = hits.data?.length === 1 && dq.toLowerCase() === code.toLowerCase() ? hits.data[0] : null;
      if (one) return add(one);
      toast(`Nothing has the barcode or SKU “${code}”. Add it as a product first.`, 'error');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That barcode couldn’t be looked up.', 'error');
    }
  };
  useSheetScanner((code) => { void addCode(code); });
  const save = async () => {
    const r = await run(() => post('/api/stock/receive', {
      lines: lines.filter((l) => l.qty > 0).map((l) => ({ productId: l.id, qty: l.qty, ...(editCost && l.cost !== null && l.cost !== l.costWas ? { costCents: l.cost } : {}) })),
      note: note.trim() || null,
    }), { success: `Received ${units} item${units === 1 ? '' : 's'}.` });
    if (r) onDone();
  };
  // Scanned items aren't thrown away by a stray tap outside the window, or Escape. (The window
  // keeps the close it was opened with for Escape, so this one reads the count as it is now.)
  const unitsNow = useRef(units);
  unitsNow.current = units;
  const close = useCallback(() => (unitsNow.current > 0 ? setLeaving(true) : onClose()), [onClose]);
  return (
    <Sheet
      title="Receive delivery"
      wide
      onClose={close}
      footer={<><Button onClick={close}>Cancel</Button><Button variant="primary" icon="check" busy={busy} disabled={!units || tooMany} onClick={save}>{units ? `Receive ${units} item${units === 1 ? '' : 's'}` : 'Receive'}</Button></>}
    >
      <div className="stack" data-enter="off">
        <p className="muted small" style={{ margin: 0 }}>Scan each item’s barcode: every scan adds one. Or type a name and pick it. Fix a count with − and +, then save it all at once{me?.location ? ` into ${me.location.name}’s stock` : ''}.</p>
        <div className="search">
          <Icon.scan />
          <input
            className="input input-lg" autoFocus={!first} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Scan a barcode, or type a name or SKU" aria-label="Scan or search items"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const code = q; setQ(''); void addCode(code); } }}
          />
        </div>
        {dq.length >= 2 && hits.data?.length ? (
          <div className="list" aria-label="Items found">
            {hits.data.map((p: any) => (
              <button key={p.id} type="button" className="list-row" onClick={() => { add(p); setQ(''); }}>
                <span className="main-col"><span className="t" style={{ display: 'block' }}>{p.name}</span><span className="s" style={{ display: 'block' }}>{[p.sku, p.barcode].filter(Boolean).join(' · ') || p.category_name}</span></span>
                <span className="small muted">{p.qty ?? 0} here</span>
                <Icon.plus />
              </button>
            ))}
          </div>
        ) : null}
        {lines.length ? (
          <div className="list" aria-label="In this delivery">
            {lines.map((l) => (
              <div key={l.id} className="list-row" style={{ cursor: 'default', flexWrap: 'wrap', background: lastId === l.id ? 'var(--blue-soft)' : undefined }}>
                <span className="main-col">
                  <span className="t" style={{ display: 'block' }}>{l.name}</span>
                  <span className="s" style={{ display: 'block' }}>{[l.codes, `${l.here} here now`].filter(Boolean).join(' · ')}</span>
                </span>
                <div className="qty">
                  <button type="button" aria-label={`One less ${l.name}`} disabled={l.qty <= 0} onClick={() => setQty(l.id, l.qty - 1)}><Icon.minus /></button>
                  <input
                    ref={l.id === first?.id ? firstQty : undefined}
                    className="input" inputMode="numeric" data-enter="off" placeholder="0" aria-label={`How many ${l.name} came in`} value={l.qty ? String(l.qty) : ''}
                    style={{ width: 72, minHeight: 40, height: 40, border: 0, borderRadius: 0, padding: '0 4px', textAlign: 'center', fontWeight: 700 }}
                    onChange={(e) => setQty(l.id, Number(e.target.value.replace(/\D/g, '').slice(0, 5)) || 0)}
                    onKeyDown={(e) => guard(e, (code, before) => { setQty(l.id, Number(before.replace(/\D/g, '')) || 0); void addCode(code); })}
                  />
                  <button type="button" aria-label={`One more ${l.name}`} onClick={() => setQty(l.id, l.qty + 1)}><Icon.plus /></button>
                </div>
                {editCost ? (
                  <div style={{ width: 132 }} title="Cost each: change it only if it changed">
                    <MoneyInput value={l.cost} onChange={(v) => setLines((ls) => ls.map((x) => (x.id === l.id ? { ...x, cost: v } : x)))} ariaLabel={`Cost each for ${l.name}`} />
                  </div>
                ) : null}
                <button type="button" className="icon-btn" aria-label={`Take ${l.name} off the list`} onClick={() => setLines((ls) => ls.filter((x) => x.id !== l.id))}><Icon.trash /></button>
                {l.qty > MAX_LINE_QTY ? <div className="error-text small" style={{ flexBasis: '100%' }}>Up to 10,000 at a time. Check the number.</div> : null}
              </div>
            ))}
          </div>
        ) : <Empty icon="scan" title="Scan the first item." />}
        {editCost && lines.length ? <p className="small muted" style={{ margin: 0 }}>The dollar box is the cost each. Change it only if the supplier’s price changed.</p> : null}
        <Field label="Note (optional)">
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. MobileSentrix order #4471" />
        </Field>
      </div>
      {leaving ? createPortal(
        <Confirm title="Leave without receiving?" confirmLabel="Leave" danger onClose={() => setLeaving(false)} onConfirm={onClose}>
          <p>The {units} item{units === 1 ? '' : 's'} scanned so far won’t be received.</p>
        </Confirm>,
        document.body,
      ) : null}
    </Sheet>
  );
}

// ---------------- low stock ----------------

function LowTab({ loc }: { loc: string }) {
  const list = useLoad(() => get(`/api/products${qs({ location: loc, low: 1 })}`), [loc]);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <section className="stack">
      {list.loading && !list.data ? <Spinner /> : null}
      {list.data ? (
        list.data.length ? (
          <div className="list">
            {list.data.map((p: any) => (
              <button key={p.id} type="button" className="list-row" onClick={() => setOpen(p.id)}>
                <span className="thumb"><ProductPicture url={p.image_url} categoryId={p.category_id} name={p.name} size={56} /></span>
                <span className="main-col"><span className="t" style={{ display: 'block' }}>{p.name}</span><span className="s" style={{ display: 'block' }}>{p.category_name}{p.supplier_name ? ` · ${p.supplier_name}` : ''}</span></span>
                <span className="right"><span className="strong" style={{ display: 'block' }}>Quantity: {loc === 'all' ? p.total_qty : p.qty ?? 0}</span><span className="small muted">alert at {p.min_qty}</span></span>
                <Badge tone="amber">LOW STOCK</Badge>
              </button>
            ))}
          </div>
        ) : (
          <Empty icon="check" title="Nothing is low. Nice." />
        )
      ) : null}
      {open ? <ProductSheet id={open} onClose={() => setOpen(null)} onChanged={list.reload} /> : null}
    </section>
  );
}

// ---------------- transfers ----------------

function TransfersTab() {
  const { me, can } = useSession();
  const list = useLoad(() => get('/api/transfers'), []);
  const [creating, setCreating] = useState(false);
  const { run } = useAction();
  const act = async (id: string, what: 'receive' | 'cancel') => {
    const r = await run(() => post(`/api/transfers/${id}/${what}`), { success: what === 'receive' ? 'Received. Stock added.' : 'Cancelled. Items are back in stock.' });
    if (r) list.reload();
  };
  return (
    <section className="stack">
      <div className="row-wrap">
        <p className="muted grow">Stock leaves the sending store right away and arrives when the receiving store taps Receive.</p>
        {can('inventory.transfer') ? <Button variant="primary" icon="truck" onClick={() => setCreating(true)}>New transfer</Button> : null}
      </div>
      {list.data?.length ? (
        <div className="list">
          {list.data.map((t: any) => (
            <div key={t.id} className="list-row" style={{ cursor: 'default', flexWrap: 'wrap' }}>
              <span className="main-col">
                <span className="t" style={{ display: 'block' }}>Transfer #{t.transfer_no}: {t.from_name} → {t.to_name}</span>
                <span className="s" style={{ display: 'block' }}>{t.lines.map((l: any) => (l.product_id ? `${l.qty} × ${l.name}` : l.name)).join(', ')}</span>
                <span className="s" style={{ display: 'block' }}>Sent by {t.created_by_name} · {formatDateTime(t.created_at)}{t.received_at ? ` · received by ${t.received_by_name} ${formatDateTime(t.received_at)}` : ''}</span>
              </span>
              {t.status === 'in_transit' ? <Badge tone="blue" icon="truck">On the way</Badge> : t.status === 'received' ? <Badge tone="green" icon="check">Received</Badge> : <Badge>Cancelled</Badge>}
              {t.status === 'in_transit' && t.to_location_id === me?.location?.id ? <Button variant="primary" onClick={() => act(t.id, 'receive')}>Receive</Button> : null}
              {t.status === 'in_transit' && t.from_location_id === me?.location?.id && can('inventory.transfer') ? <Button variant="danger" onClick={() => act(t.id, 'cancel')}>Cancel</Button> : null}
            </div>
          ))}
        </div>
      ) : list.data ? (
        <Empty icon="truck" title="No transfers yet." />
      ) : <Spinner />}
      {creating ? <TransferSheet onClose={() => setCreating(false)} onDone={() => { setCreating(false); list.reload(); }} /> : null}
    </section>
  );
}

function TransferSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { me } = useSession();
  const { run, busy } = useAction();
  const others = (me?.locations ?? []).filter((l) => l.id !== me?.location?.id);
  const [to, setTo] = useState(others[0]?.id ?? '');
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 200);
  const [lines, setLines] = useState<{ key: string; productId?: string; deviceId?: string; name: string; qty: number; max?: number }[]>([]);
  const products = useLoad(() => (dq.length >= 2 ? get(`/api/products${qs({ q: dq, limit: 8 })}`) : Promise.resolve([])), [dq]);
  const devices = useLoad(() => (dq.length >= 2 ? get(`/api/devices${qs({ q: dq, limit: 8 })}`) : Promise.resolve([])), [dq]);
  const hits = useMemo(() => [...(devices.data ?? []).map((d: any) => ({ key: `d${d.id}`, deviceId: d.id, name: `${d.title} · ${d.imei ? `IMEI …${d.imei.slice(-4)}` : d.serial}`, qty: 1 })), ...(products.data ?? []).map((p: any) => ({ key: `p${p.id}`, productId: p.id, name: p.name, qty: 1, max: p.qty ?? 0 }))], [products.data, devices.data]);
  const add = (h: any) => {
    if (lines.some((l) => l.key === h.key)) return;
    setLines([...lines, h]);
    setQ('');
  };
  const submit = async () => {
    const r = await run(() => post('/api/transfers', { toLocationId: to, lines: lines.map((l) => (l.deviceId ? { deviceId: l.deviceId } : { productId: l.productId, qty: l.qty })) }), { success: 'Transfer sent.' });
    if (r) onDone();
  };
  return (
    <Sheet title={`Send stock from ${me?.location?.name}`} onClose={onClose} wide footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} disabled={!to || !lines.length} onClick={submit}>Send transfer</Button></>}>
      <Field label="Send to">
        <select className="select" value={to} onChange={(e) => setTo(e.target.value)}>{others.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
      </Field>
      <div className="search">
        <Icon.search />
        <input className="input" placeholder="Scan or search products and phones" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find items to send" autoFocus />
      </div>
      {hits.length ? (
        <div className="list">
          {hits.map((h: any) => (
            <button key={h.key} type="button" className="list-row" onClick={() => add(h)}>
              <span className="main-col t">{h.name}</span>
              {h.max !== undefined ? <span className="small muted">{h.max} here</span> : null}
              <Icon.plus />
            </button>
          ))}
        </div>
      ) : null}
      <h3>Sending</h3>
      {lines.length ? (
        <div className="list">
          {lines.map((l) => (
            <div key={l.key} className="list-row" style={{ cursor: 'default' }}>
              <span className="main-col t">{l.name}</span>
              {l.productId ? (
                <div className="qty">
                  <button type="button" aria-label="Less" onClick={() => setLines(lines.map((x) => (x.key === l.key ? { ...x, qty: Math.max(1, x.qty - 1) } : x)))}><Icon.minus /></button>
                  <span>{l.qty}</span>
                  <button type="button" aria-label="More" onClick={() => setLines(lines.map((x) => (x.key === l.key ? { ...x, qty: Math.min(MAX_LINE_QTY, x.qty + 1) } : x)))}><Icon.plus /></button>
                </div>
              ) : <Badge>1 device</Badge>}
              <button type="button" className="icon-btn" aria-label={`Remove ${l.name}`} onClick={() => setLines(lines.filter((x) => x.key !== l.key))}><Icon.trash /></button>
            </div>
          ))}
        </div>
      ) : <p className="muted small">Nothing added yet.</p>}
    </Sheet>
  );
}

// ---------------- supplier link & list import ----------------

function SupplierLinkSheet({ onFound, onClose }: { onFound: (p: Prefill) => void; onClose: () => void }) {
  const { run, busy } = useAction();
  const [url, setUrl] = useState('');
  const [found, setFound] = useState<Prefill | null>(null);
  const read = async () => {
    const r = await run(() => post('/api/products/from-link', { url }));
    if (r) setFound(r);
  };
  return (
    <Sheet
      title="Add from a supplier link"
      onClose={onClose}
      wide
      footer={found ? <><Button onClick={() => setFound(null)}>Try another link</Button><Button variant="primary" onClick={() => onFound(found)}>Continue</Button></> : <><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} disabled={url.trim().length < 10} onClick={read}>Read the page</Button></>}
    >
      {!found ? (
        <>
          <p className="muted">Open the part on your supplier's website (PhoneLCDParts, Mello and others), copy the address, and paste it here. STORE OS reads the name, picture and price, and the price becomes your cost.</p>
          <Field label="Link to the part"><input className="input input-lg" autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.phonelcdparts.com/..." /></Field>
          <p className="small muted">Some sites (like FactoryPhoneParts) don't share product details with other programs. For those, add the part by hand and take a photo.</p>
        </>
      ) : (
        <div className="row" style={{ gap: 16, alignItems: 'flex-start' }}>
          <span className="thumb" style={{ width: 120, height: 120 }}><ProductPicture url={found.imageUrl} categoryId="parts" name={found.name ?? 'Part'} size={120} /></span>
          <dl className="kv grow">
            <dt>Name</dt><dd className="strong">{found.name ?? '—'}</dd>
            <dt>Your cost</dt><dd>{found.costCents !== null ? formatCents(found.costCents) : 'Not on the page'}</dd>
            <dt>SKU</dt><dd>{found.sku ?? '—'}</dd>
            {found.barcode ? <><dt>Barcode</dt><dd>{found.barcode}</dd></> : null}
          </dl>
        </div>
      )}
    </Sheet>
  );
}

function ImportSheet({ onDone, onClose }: { onDone: () => void; onClose: () => void }) {
  const { me } = useSession();
  const toast = useToast();
  const { run, busy } = useAction();
  const suppliers = useLoad(() => get('/api/suppliers'), []);
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [categoryId, setCategoryId] = useState('parts');
  const [markup, setMarkup] = useState('100');
  const [addStock, setAddStock] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const body = (commit: boolean) => ({ csv, supplierId: supplierId || null, categoryId, markupPct: Number(markup || 0), addStock, commit });
  const loadFile = (file: File | undefined) => {
    if (!file) return;
    if (!/\.(csv|txt|tsv)$/i.test(file.name)) return toast('Save the sheet as CSV first (File → Save As → CSV), then pick it here.', 'error');
    const reader = new FileReader();
    reader.onload = () => { setCsv(String(reader.result ?? '')); setFileName(file.name); setPreview(null); };
    reader.readAsText(file);
  };
  const check = async () => {
    const r = await run(() => post('/api/products/import', body(false)));
    if (r) setPreview(r);
  };
  const commit = async () => {
    const r = await run(() => post('/api/products/import', body(true)));
    if (r) {
      toast(`Imported: ${r.summary.create} new, ${r.summary.update} updated.`, 'good');
      onDone();
    }
  };
  return (
    <Sheet
      title="Import a supplier list"
      onClose={onClose}
      wide
      footer={preview ? <><Button onClick={() => setPreview(null)}>Back</Button><Button variant="primary" busy={busy} disabled={!preview.summary.create && !preview.summary.update} onClick={commit}>Import {preview.summary.create + preview.summary.update} items</Button></> : <><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} disabled={!csv.trim()} onClick={check}>Check the list</Button></>}
    >
      {!preview ? (
        <>
          <p className="muted">Use a spreadsheet from your supplier or your own. It needs a <strong>Name</strong> column. STORE OS also reads SKU, Barcode, Cost, Price, Image (picture link), Model (what it fits) and Qty. Items already in inventory get the new cost and picture.</p>
          <div className="form-grid">
            <Field label="Supplier">
              <select className="select" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                <option value="">—</option>
                {(suppliers.data ?? []).map((x: any) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </Field>
            <Field label="These are">
              <select className="select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="parts">Repair parts</option>
                <option value="accessories">Accessories</option>
                <option value="perfumes">Perfume</option>
              </select>
            </Field>
            <Field label="Selling price when the list has none" hint="markup on cost, %"><input className="input" inputMode="numeric" value={markup} onChange={(e) => setMarkup(e.target.value.replace(/\D/g, ''))} /></Field>
            <label className="check"><input type="checkbox" checked={addStock} onChange={(e) => setAddStock(e.target.checked)} /> Add the Qty column to {me?.location?.name ?? 'this store'}'s stock</label>
          </div>
          <Field label="CSV file"><input className="input" type="file" accept=".csv,.txt,.tsv,text/csv" onChange={(e) => loadFile(e.target.files?.[0])} /></Field>
          {fileName ? <p className="small muted">Loaded {fileName}.</p> : null}
          <Field label="Or paste the rows here" hint="copy from Excel or Google Sheets, header row first"><textarea className="input" rows={6} value={csv} onChange={(e) => { setCsv(e.target.value); setPreview(null); }} /></Field>
        </>
      ) : (
        <>
          <div className="stat-row">
            <div className="stat"><div className="k">New items</div><div className="v">{preview.summary.create}</div></div>
            <div className="stat"><div className="k">Updated</div><div className="v">{preview.summary.update}</div></div>
            <div className="stat"><div className="k">Skipped</div><div className="v">{preview.summary.skip}</div></div>
          </div>
          <p className="small muted">Columns found: {preview.columns.join(', ')}</p>
          <div className="list" style={{ maxHeight: 360, overflow: 'auto' }}>
            <table className="table">
              <thead><tr><th>Row</th><th /><th>Name</th><th className="right">Cost</th><th className="right">Qty</th><th>Result</th></tr></thead>
              <tbody>
                {preview.rows.map((r: any) => (
                  <tr key={r.row}>
                    <td className="small muted">{r.row}</td>
                    <td><span className="thumb" style={{ width: 40, height: 40 }}><ProductPicture url={r.image_url} categoryId={categoryId} name={r.name} size={40} /></span></td>
                    <td><div className="strong">{r.name || '—'}</div><div className="small muted">{r.sku ?? ''}</div></td>
                    <td className="right">{r.cost_cents !== null ? <Cost cents={r.cost_cents} label={false} /> : '—'}</td>
                    <td className="right">{r.qty || ''}</td>
                    <td>{r.action === 'create' ? <Badge tone="green">New</Badge> : r.action === 'update' ? <Badge tone="blue">Update</Badge> : <Badge tone="amber">Skip</Badge>}{r.problem ? <div className="tiny muted">{r.problem}</div> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Sheet>
  );
}

const TEST_CHECKS = [
  'Turns on, no restarts', 'Screen and touch (no dead spots or burn-in)', 'Face ID / Touch ID', 'Front and back cameras', 'Speakers and microphones',
  'Charging port (and wireless charging)', 'Buttons and switches', 'Wi-Fi, Bluetooth and cell signal', 'Find My / Google account removed', 'Erased (factory reset)',
];

/** A phone bought from a customer is tested before it goes on sale. */
function TestingPanel({ device, onDone, onWholesale }: { device: any; onDone: () => void; onWholesale?: () => void }) {
  const { can, me } = useSession();
  const print = usePrint();
  const { run, busy } = useAction();
  const [done, setDone] = useState<string[]>([]);
  const [battery, setBattery] = useState(device.battery_health ? String(device.battery_health) : '');
  const [grade, setGrade] = useState(device.grade ?? '');
  const [price, setPrice] = useState<number | null>(device.price_cents || device.suggested_price_cents || null);
  const allDone = done.length === TEST_CHECKS.length;
  const batteryBad = !!battery && (Number(battery) < 1 || Number(battery) > 100);
  // Skip the checklist: straight into inventory with this price and grade (other roles need a manager's PIN).
  // Either way it goes on sale, its price sticker prints right away.
  const stockNow = async () => {
    const r = await run(
      (approval?: ApprovalInput) => post(`/api/devices/${device.id}/stock`, { priceCents: price, grade: grade || null, batteryHealth: battery ? Number(battery) : null, approval }),
      { success: held ? 'In inventory. It can be sold when the hold ends. Its price sticker is printing.' : 'In inventory and ready to sell. Its price sticker is printing.' },
    );
    if (r) {
      printPriceStickers(print, [r], me?.settings);
      onDone();
    }
  };
  const canFinish = can('inventory.adjust') && can('device.edit_price') && can('device.add');
  const held = device.hold_until && device.hold_until > Date.now();
  const finish = async () => {
    const stamp = new Date().toLocaleDateString('en-US');
    const notes = [device.notes, `Tested ${stamp}: all ${TEST_CHECKS.length} checks passed.`].filter(Boolean).join('\n');
    const r = await run(
      () => patch(`/api/devices/${device.id}`, { status: 'in_stock', priceCents: price, grade: grade || null, batteryHealth: battery ? Number(battery) : null, notes }),
      { success: held ? 'Ready. It can be sold when the hold ends. Its price sticker is printing.' : 'Ready to sell. Its price sticker is printing.' },
    );
    if (r) {
      printPriceStickers(print, [r], me?.settings);
      onDone();
    }
  };
  return (
    <div className="card stack-sm">
      <div className="spread"><h3>Test before it goes on sale</h3>{device.source_ref && (device.source === 'buyback' || device.source === 'trade_in') ? <a href={`#/buybacks/${device.source_ref}`}>Purchase record</a> : null}</div>
      {held ? <div className="banner warn"><Icon.clock /><div>Held until {formatDateTime(device.hold_until)} (the hold for bought phones, set in Settings → Buy & trade-in). You can test and price it now.</div></div> : null}
      <div className="checklist">
        {TEST_CHECKS.map((c) => (
          <label key={c} className="check"><input type="checkbox" checked={done.includes(c)} onChange={(e) => setDone(e.target.checked ? [...done, c] : done.filter((x) => x !== c))} /> {c}</label>
        ))}
      </div>
      <div className="form-grid">
        <Field label="Battery health %" hint={batteryBad ? '1 to 100' : 'prints on the price sticker'}><input className="input" inputMode="numeric" value={battery} onChange={(e) => setBattery(e.target.value.replace(/\D/g, '').slice(0, 3))} /></Field>
        <Field label="Grade">
          <select className="select" value={grade} onChange={(e) => setGrade(e.target.value)}>
            <option value="">—</option>
            {Object.entries(GRADE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Selling price"><MoneyInput value={price} onChange={setPrice} /></Field>
      </div>
      <div className="row-wrap">
        {canFinish ? (
          <Button variant="primary" icon="check" busy={busy} disabled={!allDone || !price || batteryBad} onClick={finish}>{allDone ? 'All checks passed: ready to sell' : `Check all ${TEST_CHECKS.length} items first`}</Button>
        ) : null}
        <Button variant={canFinish ? 'secondary' : 'primary'} icon="boxes" busy={busy} disabled={!price || batteryBad} onClick={stockNow}>Add to inventory now</Button>
        {onWholesale ? <Button icon="truck" onClick={onWholesale}>Wholesale</Button> : null}
      </div>
      <p className="small muted">{price ? '“Add to inventory now” skips the checklist.' : 'Type the selling price first.'}{canFinish ? '' : ' A manager approves the price with their PIN.'}{device.suggested_price_cents && !device.price_cents ? ` The price is the last one like it: ${formatCents(device.suggested_price_cents)}.` : ''}</p>
    </div>
  );
}
